// Cartagena, "Clock tower over the old walls": a warm, damp November night in the walled city, just after a shower.
// Where you stand: at the north end of the Plaza de los Coches inside the walls, by the mouth of the Calle de las
// Carretas (about 10.4235 N, 75.5494 W), looking south-south-east (bearing ~165°) across the wet brick plaza to the
// inner (north-west) face of the Puerta del Reloj, 54 m away. The Torre del Reloj rises over the gate's central passage,
// with a side passage either side; the yellow inner face of the walls runs off to the left (north-east, a little nearer)
// and to the right (south-west, receding to the plaza's far corner). The bronze Pedro de Heredia stands on his pedestal
// 25 m away, right of the tower from here. On the right, the arcade of the Portal de los Dulces with its sweet stalls
// (cheat: that row of houses really recedes steeply along the plaza's west side; it is drawn frontally, as the right-hand
// slice). Over the wall: palm crowns on the Plaza de la Paz outside the gate, the haze of the avenue's lights, and to the
// left a rooftop sign on a La Matuna building (cheat: those buildings stand a little further left, east, than drawn).
// Bocagrande's and Manga's towers are 1-2 km off and sit below the wall's line from here, so none show.
// Back to front: deep sky, stars, the moon, two thin wisps -> the rooftop sign and the palms beyond the wall -> the
// floodlit Torre del Reloj and the yellow gate wall with its three arches and lanterns -> the wet plaza mirroring them ->
// a waiting horse carriage, a raspao cart, strollers, the statue -> the Portal de los Dulces (sweet stalls under the
// arcade, a long wooden balcony with bougainvillea) -> an almond tree and a five-globe lamp on the left -> petals.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, inEvent, cycle, hash, rng, bayer, hex,
  makeCanvas, px, rect, ditherFill, makeHalo, drawHalo, FONT3, reflect, streak, makePitch, gradeAd, hands } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                          // this scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const TX = 196;                                   // the Torre del Reloj's axis column
// Rows of the tower, from the weathervane down to the wall top it stands on (measured shares in renderTower)
const TR = { vane: 4, orb: 9, spire: 13, gab: 37, upTop: 45, clkTop: 66, dial: 80, clkBot: 96, ped: 97, band: 109, baseBot: 126 };
const WALL_TOP = 127, WALL_FOOT = 184;            // the gate wall's coping row at the tower and its foot on the plaza
const GATE = [                                    // the three passages: left edge, width, springing row
  { x: 108, w: 21, spring: 167 }, { x: 184, w: 25, spring: 164 }, { x: 256, w: 19, spring: 167 },
];
const LANTERNS = [90, 156, 234, 318];            // wall lanterns between the arches (bracket column)
const LANTERN_Y = 141;
const MOON = { x: 322, y: 32, r: 6 };
const BB = { x: 112, y: 86, w: 48, h: 21 };       // the rooftop sign's face beyond the wall (the pitch is painted exactly here)
const BB_ROOF = 113;                              // its building's roof row
const STATUE = { x: 292, base: 196 };             // Pedro de Heredia: axis column and the plinth's ground row
const PORTAL = { x0: 352, x1: 480, floor: 205 };  // the Portal de los Dulces slice: left edge and its arcade's floor row
const CORAL = { x1: 440, eave: 114, rail: 147, deck: 160, beam: 167, spring: 180 };   // the coral house: eave, balcony, arcade
const CREAM = { x0: 440, top: 80 };               // the taller cream house behind it on the right
const PIERS = [352, 382, 412, 442, 472];          // arcade piers (4 px wide, left edges)
const LAMPS = [                                   // five-globe cast-iron lamps on the plaza: column, base row, height
  { x: 58, base: 210, h: 96 }, { x: 256, base: 196, h: 58 }, { x: 344, base: 207, h: 84 },
];
const NEAR = { x0: 398, floor: 207, spring: 152, eave: 50, rail: 92, deck: 108 };   // the Portal's north bay, close on the right
const ROCK_T = 1290;                              // the clock's resting time (21:30) when render gets no clock

/* ================= Pixel helpers ================= */
const MIXC = new Map();
/** #rrggbb between two #rrggbb colours (cached) */
function mixHex(a, b, t) {
  t = clamp(t, 0, 1);
  const k = a + b + t.toFixed(3);
  let v = MIXC.get(k);
  if (!v) {
    const A = hex(a), B = hex(b);
    v = '#' + A.map((c, i) => Math.round(lerp(c, B[i], t)).toString(16).padStart(2, '0')).join('');
    MIXC.set(k, v);
  }
  return v;
}
/** ImageData painter for pixel-heavy layers: put(x, y, col, alpha 0..255) */
function painter(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data, cache = {};
  const put = (i, y, col, a = 255) => {
    i = Math.round(i); y = Math.round(y);
    if (i < 0 || y < 0 || i >= w || y >= h || !col) return;
    const rgb = cache[col] || (cache[col] = hex(col)), o = (y * w + i) * 4;
    d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = a;
  };
  const fill = (x0, y0, ww, hh, col) => { for (let y = y0; y < y0 + hh; y++) for (let i = x0; i < x0 + ww; i++) put(i, y, col); };
  return { c, x, put, fill, done: () => { x.putImageData(img, 0, 0); return c; } };
}
/** pick from a ramp (light to dark) with ordered dither: v in [0, 1] */
function pick(ramp, v, i, y) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > bayer(i, y)) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** integer Bresenham line through a put() */
function pline(put, x0, y0, x1, y1, col) {
  x0 = Math.round(x0); y0 = Math.round(y0); x1 = Math.round(x1); y1 = Math.round(y1);
  const dx = Math.abs(x1 - x0), dy = -Math.abs(y1 - y0), sx = x0 < x1 ? 1 : -1, sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  for (;;) {
    put(x0, y0, col);
    if (x0 === x1 && y0 === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) { err += dy; x0 += sx; }
    if (e2 <= dx) { err += dx; y0 += sy; }
  }
}
// signs: FONT3 with wide M, W, N and K (FONT3's read as H or Π at 1x)
const WIDE = {
  M: ['10001', '11011', '10101', '10001', '10001'], W: ['10001', '10001', '10101', '10101', '01010'],
  N: ['1001', '1101', '1011', '1001', '1001'], K: ['1001', '1010', '1100', '1010', '1001'],
};
const glyphOf = ch => WIDE[ch] ?? FONT3[ch];
const signWidth = (s, gap = 1) => [...s].reduce((w, ch) => w + (glyphOf(ch)?.[0].length ?? 3) + gap, 0) - (s.length ? gap : 0);
/** sign text through a put(): whole pixels; tall draws every row twice (condensed painted capitals) */
function signText(put, s, x, y, col, { gap = 1, tall = false, shade = null } = {}) {
  let cx = Math.round(x); y = Math.round(y);
  const k = tall ? 2 : 1;
  for (const ch of s) {
    const g = glyphOf(ch), w = g?.[0].length ?? 3;
    if (g) g.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '1') for (let q = 0; q < k; q++) put(cx + i, y + j * k + q, shade && q === k - 1 && j === g.length - 1 ? shade : col); });
    cx += w + gap;
  }
}
/** the wall's coping row and foot row at column x (the wall recedes gently to the right, comes nearer on the left) */
const wallTop = x => x <= TX - 30 ? Math.round(lerp(118, WALL_TOP, x / (TX - 30))) : x <= TX + 30 ? WALL_TOP : Math.round(lerp(WALL_TOP, 137, (x - TX - 30) / (PORTAL.x0 - TX - 30)));
const wallFoot = x => x <= TX - 30 ? Math.round(lerp(187, WALL_FOOT, x / (TX - 30))) : x <= TX + 30 ? WALL_FOOT : Math.round(lerp(WALL_FOOT, 181, (x - TX - 30) / (PORTAL.x0 - TX - 30)));

/* ================= Sky: a humid Caribbean night, deep blue overhead, the avenue's warm haze over the wall ================= */
const SKY = ['#04061a', '#060a20', '#080e26', '#0b122c', '#0e1632', '#121a38', '#161f3e', '#1b2444', '#212a4a', '#272f4e',
  '#2e3452', '#363954', '#3f3e56'];
const HAZE = ['#06071a', '#0a0b20', '#0f0f26', '#15122a', '#1c162e', '#241a32', '#2d1e34', '#372236', '#422738', '#4d2c3a',
  '#58323c', '#64383e', '#703e40'];
const SKY_W = [0, 0.25, 0.5, 0.75, 1].map(w => SKY.map((c, k) => mixHex(c, HAZE[k], w)));
const SKY_H = 136;                                // the sky canvas reaches below the lowest wall top
function renderSky() {
  const p = painter(W, SKY_H);
  for (let y = 0; y < SKY_H; y++) for (let i = 0; i < W; i++) {
    const v = Math.pow(y / (SKY_H - 1), 1.25);
    const g = clamp(1 - Math.hypot((i - 220) / 300, (y - 140) / 62), 0, 1);          // the avenue's glow beyond the gate
    const moon = Math.max(0, 1 - Math.hypot((i - MOON.x) / 80, (y - MOON.y) / 56)) * 0.2;
    const warm = clamp(g * g * 1.3, 0, 1) * 4, lv = Math.min(4, Math.floor(warm) + (frac(warm) > bayer(i + 1, y + 2) ? 1 : 0));
    p.put(i, y, pick(SKY_W[lv], v - moon * (1 - g) + g * 0.04, i, y));
  }
  return p.done();
}
const STARS = [];
{
  const r = rng(1533);
  while (STARS.length < 130) {
    const x = Math.floor(r() * W), y = 2 + Math.floor(r() * 104);
    const nearMoon = Math.hypot(x - MOON.x, y - MOON.y) < 18;
    const inTower = x > TX - 34 && x < TX + 34 && y > TR.vane - 3;
    const behindHouse = (x >= PORTAL.x0 - 2 && y > CREAM.top - 6) || x >= NEAR.x0 - 2;
    const low = y > 80 && r() < 0.6;
    if (nearMoon || inTower || behindHouse || low) continue;
    STARS.push({ x, y, k: 10 + Math.floor(r() * 46), ph: r(), b: 0.25 + r() * 0.6 * (1 - y / 140), big: r() < 0.05 });
  }
}
function renderMoon() {
  const { r } = MOON, s = r * 2 + 1, p = painter(s, s);
  const maria = [[-2, -1, 1.6], [1, 2, 1.5], [2, -2, 1.1], [-2, 3, 0.9]];
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - r, dy = y - r, dd = Math.hypot(dx, dy);
    if (dd > r + 0.35) continue;
    let col = dx + dy < -4 ? '#fffbee' : '#f7efd6';
    for (const [cx, cy, cr] of maria) if (Math.hypot(dx - cx, dy - cy) < cr) col = '#ded4b8';
    if (dd > r - 1.1) col = dx + dy > 1 ? '#cabd9e' : '#f0e6c8';
    p.put(i, y, col);
  }
  return p.done();
}
// thin moonlit wisps (dithered, no outline), drifting slowly
function renderWisp(w, h, seed) {
  const r = rng(seed), p = painter(w, h), puffs = [];
  for (let i = 0; i < 12; i++) puffs.push({ x: 6 + r() * (w - 12), y: h * (0.3 + r() * 0.45), rx: 4 + r() * 12, ry: 1 + r() * 2.2 });
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) {
    let v = 0;
    for (const q of puffs) v = Math.max(v, 1 - ((i - q.x) / q.rx) ** 2 - ((y - q.y) / q.ry) ** 2);
    v *= Math.min(1, i / 16, (w - 1 - i) / 16) * (0.7 + 0.3 * hash(i >> 2, y, seed));
    if (v * 1.2 > bayer(i, y) + 0.2) p.put(i, y, v > 0.62 && y < h * 0.5 ? '#34405e' : v > 0.4 ? '#222a46' : '#1a203a');
  }
  return p.done();
}
const WISPS = [{ x: 250, y: 22, w: 96, h: 7, seed: 31 }, { x: 40, y: 64, w: 110, h: 8, seed: 47 }, { x: 330, y: 70, w: 70, h: 6, seed: 59 },
  { x: 120, y: 8, w: 120, h: 8, seed: 67 }, { x: 400, y: 48, w: 90, h: 6, seed: 73 }, { x: 10, y: 34, w: 80, h: 6, seed: 79 }, { x: 200, y: 92, w: 100, h: 6, seed: 83 },
  { x: 300, y: 104, w: 130, h: 7, seed: 89 }, { x: 60, y: 82, w: 90, h: 6, seed: 97 }, { x: 360, y: 14, w: 80, h: 6, seed: 101 }, { x: 170, y: 52, w: 70, h: 5, seed: 103 }];

/* ================= Beyond the wall: palm crowns on the Plaza de la Paz, a La Matuna rooftop with the sign ================= */
// a coconut palm's crown: drooping fronds with leaflets, a bent trunk down to the wall's line (it stands outside the
// gate, so only its top half shows); dark against the haze, lit from below by the avenue's sodium lamps
function renderPalm(put, cx, cy, s, seed, trunkTo) {
  const r = rng(seed), lean = (r() - 0.5) * 0.4;
  for (let y = cy + 1; y < trunkTo; y++) {
    const x = Math.round(cx + lean * (y - cy) + Math.sin((y - cy) * 0.08) * 1.2);
    put(x, y, (y & 3) ? '#1c1c22' : '#26242a'); put(x + 1, y, '#121218');
  }
  const n = 9;
  for (let f = 0; f < n; f++) {
    const a = -Math.PI + (f + 0.5) / n * Math.PI + (r() - 0.5) * 0.25, len = s * (0.8 + r() * 0.35);
    const droop = 0.9 + r() * 0.6;
    for (let k = 0; k <= len; k++) {
      const u = k / len, x = cx + Math.cos(a) * k, y = cy + Math.sin(a) * k * 0.55 + droop * u * u * s * 0.55;
      const low = Math.sin(a) > -0.35;                           // lower fronds catch the street's light
      put(x, y, low && k > 2 ? '#3a3226' : '#141c1c');
      if (k > 2 && k < len - 1 && (k & 1)) {                     // leaflets hanging off the rib
        const ll = Math.round(1 + (1 - u) * 2.2);
        for (let q = 1; q <= ll; q++) put(x + (Math.cos(a) > 0 ? -0.4 : 0.4) * q, y + q, low ? '#2a2822' : '#101616');
      }
    }
  }
  put(cx, cy, '#2a2620'); put(cx + 1, cy, '#1a1a1c'); put(cx, cy + 1, '#3a3020');   // the nut cluster
}
const PALMS = [[70, 108, 15, 11], [94, 114, 12, 13], [252, 112, 15, 17], [276, 107, 16, 19], [318, 118, 13, 23], [340, 114, 12, 29]];
function renderFar() {
  const p = painter(W, H), { put } = p;
  // the La Matuna building whose roof carries the sign: a dark top floor, a few windows still lit, a water tank
  for (let x = BB.x - 16; x <= BB.x + BB.w + 14; x++) for (let y = BB_ROOF; y < wallTop(x) + 1; y++) {
    const j = y - BB_ROOF;
    put(x, y, j === 0 ? '#34303e' : j === 1 ? '#16141e' : pick(['#24202e', '#1e1a28', '#181522'], (x - BB.x + 16) / 90 + j * 0.05, x, y));
  }
  for (let x = BB.x - 12; x < BB.x + BB.w + 12; x += 6) {
    const lit = hash(x, 3, 101) < 0.35;
    put(x, BB_ROOF + 4, lit ? '#e8b468' : '#2c2834'); put(x + 1, BB_ROOF + 4, lit ? '#c8904c' : '#2c2834');
  }
  for (let y = 105; y < BB_ROOF; y++) for (let x = BB.x - 13; x < BB.x - 5; x++) {
    const u = (x - BB.x + 9.5) / 4;
    put(x, y, y === 105 ? '#4a4652' : y >= 110 ? (x === BB.x - 13 || x === BB.x - 6 ? '#26242e' : null) : pick(['#56525e', '#423e4c', '#302c3a'], 0.3 + u * 0.5, x, y));
  }
  return p.done();
}

/* ================= The gate wall's inner face: yellow lime-wash over coral stone, three plain round passages ================= */
// From the plaza the Puerta del Reloj is a long yellow wall (the gate's outer face, with its paired Tuscan columns, is
// on the other side): the central passage under the tower, a side passage either side (opened in the 1880s), black
// lanterns on brackets between them, two white marble plaques, rain stains running down from the coping.
const FLAKES = [[22, 152, 9, 5], [70, 168, 7, 4], [141, 150, 6, 7], [236, 162, 6, 4], [312, 160, 8, 5], [333, 147, 5, 3], [52, 138, 5, 3]];   // patches where the wash has flaked
const GW = ['#f2c66c', '#e0ae58', '#c8944a', '#a87a40', '#865e36', '#62462e', '#423028'];       // lit wash, light to dark
const ARCH = ['#ffd890', '#f2b468', '#cc8a46', '#90583a', '#563428', '#2e1e22', '#1c1218'];     // a passage: its far opening's light to the vault
function renderWall(p) {
  const { put } = p;
  for (let x = 0; x < PORTAL.x0 + 4; x++) {
    const top = wallTop(x), foot = wallFoot(x), gate = x >= 102 && x <= 292;
    for (let y = top; y < foot; y++) {
      const f = (y - top) / (foot - top);
      if (y === top) { put(x, y, gate ? '#f6d898' : '#dcb87c'); continue; }
      if (y === top + 1) { put(x, y, '#7a5634'); continue; }
      let v = 0.7 - f * 0.3 + (gate ? -0.05 : 0.05);
      for (const L of LANTERNS) v -= Math.max(0, 1 - Math.hypot((x - L) / 26, (y - LANTERN_Y - 6) / 24)) * 0.36;
      v -= Math.max(0, 1 - Math.hypot((x - LAMPS[1].x) / 40, (y - foot) / 30)) * 0.18;           // the plaza lamp by the gate
      v -= Math.max(0, 1 - Math.hypot((x - TX) / 50, (y - top) / 26)) * 0.14;                     // the tower's floodlights spill down
      if (hash(x >> 1, 7, 211) < 0.22) v += 0.1 * (1 - f) * (1 - f);                              // stains from the coping
      const spout = (x - 30) % 52;                                                                 // a spout's long dark streak
      if (spout >= 0 && spout < 3 && f < 0.75) v += 0.16 * (1 - f / 0.75) * (spout === 1 ? 1 : 0.6);
      if ((y - top) % 7 === 0 && hash(x >> 3, y, 215) < 0.5) v += 0.05;                            // the coursing under the wash
      if (FLAKES.some(([fx, fy, rx, ry]) => ((x - fx) / rx) ** 2 + ((y - fy) / ry) ** 2 + (hash(x >> 1, y >> 1, 216) - 0.5) * 0.9 < 1)) {   // flaked wash: coral stone
        const jy = (y + Math.floor(x / 5)) % 4 === 0, jx = (x + (Math.floor(y / 4) & 1) * 3) % 6 === 0;
        put(x, y, jy || jx ? '#6a5444' : pick(['#d8bc94', '#bca07c', '#9e8668', '#7e6a56'], 0.15 + f * 0.35 + hash(x, y, 217) * 0.3, x, y));
        continue;
      }
      if (y >= foot - 3) v += 0.12;                                                                // the damp foot
      const damp = hash(x >> 3, 9, 213), dh = 6 + damp * 14;                                        // mouldy patches rising from it
      if (damp < 0.45 && y > foot - dh && hash(x, y >> 1, 214) < 0.75 - (foot - y) / dh * 0.5) v += 0.1;
      v += hash(x, y, 212) * 0.06;
      put(x, y, pick(GW, v, x, y));
    }
    if (x === 102 || x === 292) for (let y = top + 2; y < foot; y++) put(x, y, pick(GW, 0.15 + (y - top) / 200, x, y));   // the gate's quoins
    if (x === 103 || x === 293) for (let y = top + 2; y < foot; y++) put(x, y, pick(GW, 0.62, x, y));
  }
  for (let x = 31; x < PORTAL.x0; x += 52) { const t0 = wallTop(x); put(x, t0 + 2, '#2a1e1c'); put(x + 1, t0 + 2, '#3a2a24'); put(x, t0 + 3, '#1a1214'); }   // spouts
  // the three passages: voussoir ring, the dark vault, the lit far opening onto the Plaza de la Paz
  for (const G of GATE) {
    const cx = G.x + (G.w - 1) / 2, r = G.w / 2, foot = wallFoot(Math.round(cx));
    for (let y = G.spring - Math.ceil(r); y < foot; y++) for (let x = G.x; x < G.x + G.w; x++) {
      const inside = y >= G.spring || Math.hypot(x - cx, y - G.spring) <= r;
      if (!inside) continue;
      const ring = y < G.spring ? Math.hypot(x - cx, y - G.spring) > r - 1.3 : (x === G.x || x === G.x + G.w - 1);
      if (ring) { put(x, y, x < cx ? '#f4e0b0' : '#c8a476'); continue; }
      const v = clamp(Math.hypot((x - cx) / (r * 0.75), (y - foot + 6) / (r * 1.25)) * 0.9 + (y < G.spring ? 0.15 : 0), 0, 1);
      put(x, y, pick(ARCH, v, x, y));
    }
    for (let x = G.x + 2; x < G.x + G.w - 2; x++) put(x, foot - 1, '#8a6040');                     // the passage's lit floor
    put(Math.round(cx), G.spring - Math.ceil(r) - 1, '#f6e4b8');                                     // keystone
  }
  // a lamp post and a palm trunk seen through the central passage, out on the Plaza de la Paz
  const C = GATE[1], ccx = C.x + 10;
  for (let y = C.spring - 2; y < WALL_FOOT - 3; y++) put(ccx + 4, y, '#3a2420');
  put(ccx + 3, C.spring - 3, '#ffe0a0'); put(ccx + 4, C.spring - 3, '#ffe0a0'); put(ccx + 5, C.spring - 3, '#ffe0a0');
  for (let y = C.spring - 4; y < WALL_FOOT - 2; y++) put(ccx - 5, y, (y & 2) ? '#4a3026' : '#3a2620');
  // white marble plaques and the lanterns' brackets (their glass is lit per frame)
  for (const [x0, y0] of [[150, 145], [224, 145]]) for (let y = y0; y < y0 + 4; y++) for (let x = x0; x < x0 + 6; x++)
    put(x, y, x === x0 || y === y0 ? '#f4ecd8' : x === x0 + 5 || y === y0 + 3 ? '#a8987c' : (y === y0 + 1 && (x & 1)) ? '#b8a88c' : '#e2d6bc');
  for (const L of LANTERNS) {
    put(L, LANTERN_Y - 4, '#1a1214'); put(L + 1, LANTERN_Y - 4, '#1a1214'); put(L + 2, LANTERN_Y - 3, '#1a1214');
    for (let y = LANTERN_Y - 3; y < LANTERN_Y + 5; y++) put(L + 3, y, '#140e10');                  // the bracket's plate on the wall
    put(L - 1, LANTERN_Y - 2, '#1a1214'); put(L, LANTERN_Y - 2, '#2a2024'); put(L + 1, LANTERN_Y - 2, '#1a1214');
    for (let y = LANTERN_Y - 1; y < LANTERN_Y + 3; y++) { put(L - 1, y, '#1a1214'); put(L + 1, y, '#1a1214'); }
    put(L - 1, LANTERN_Y + 3, '#1a1214'); put(L, LANTERN_Y + 3, '#1a1214'); put(L + 1, LANTERN_Y + 3, '#1a1214'); put(L, LANTERN_Y + 4, '#1a1214');
  }
}

// the wall walk's outer parapet, set back behind the inner coping and lit less; people stroll between the two
function renderParapet() {
  const p = painter(W, H), { put } = p;
  for (let x = 0; x < PORTAL.x0; x++) {
    const t0 = wallTop(x);
    put(x, t0 - 5, '#c8a87a'); put(x, t0 - 4, '#a8885e');
    for (let y = t0 - 3; y < t0; y++) put(x, y, pick(['#8a6a48', '#745838', '#5e4630'], (y - t0 + 3) / 3 + hash(x, y, 218) * 0.2, x, y));
  }
  return p.done();
}

/* ================= The Torre del Reloj (the hero), floodlit cream-gold over the central passage ================= */
// Massing after photos of the plaza side (Commons: "Atardecer Torre del Reloj CTG 2019", "Puerta del Reloj (Clock Gate)
// and Plaza de los Coches"): on the wall a broad base stage with two low white-edged pediments and a small arched window;
// the clock stage, a dial between two tall arched louvres, white corner pilasters, pinnacles on its cornice and a little
// pediment over the dial; the octagonal belfry with three pointed louvred windows; a ring of gablets and pinnacles round
// the foot of the slender octagonal spire, lucarnes, an orb and the weathervane. Measured against the clock stage's
// width (37 px): the tower over the wall to the orb 117 px = 3.16 (real ~3.1), the base stage 61 px = 1.65 (real 1.65);
// shares of that height: base 30/117 (real .26), clock 31/117 (.27), belfry 21/117 (.18), spire 32/117 (.27).
const TY = ['#fff7dc', '#ffeeb4', '#fbe08a', '#f2ca6a', '#dcaa52', '#b88642', '#8a6036', '#5a3e2e'];   // floodlit wash
const TWT = ['#fffdf6', '#f8f0dc', '#e6d8bc', '#c4b294', '#968470'];                                  // white trim
function renderTower(p) {
  const { put } = p, X = TX;
  const body = (x, y, v) => put(x, y, pick(TY, v, x, y));
  /** a raking white cornice from (X ± hw, base) up to (X, apex): 2 px thick, a soft shadow under it, no stair gaps */
  const rakeRow = (x, apex, hw, base) => apex + Math.round(Math.abs(x - X) * (base - apex) / hw);
  const cornice = (apex, hw, base, below) => {
    for (let x = X - hw; x <= X + hw; x++) {
      const r = rakeRow(x, apex, hw, base), r2 = rakeRow(x + Math.sign(x - X || 1), apex, hw, base);
      put(x, r, x <= X ? TWT[0] : TWT[1]);
      for (let y = r + 1; y <= Math.max(r + 1, r2); y++) put(x, y, x <= X ? TWT[1] : TWT[2]);      // fill the step's riser
      if (below) put(x, Math.max(r + 1, r2) + 1, TY[4]);
    }
  };
  // --- the base stage (rows 97-126, 61 wide): lower block with a low gable and an arched window, a string course, a broad pediment
  const B0 = TR.ped, BAND = TR.band, BB0 = TR.baseBot, HB = 30;
  for (let x = X - HB; x <= X + HB; x++) {
    const dx = x - X, edge = dx === -HB ? -0.12 : dx >= HB - 1 ? 0.34 : dx >= HB - 3 ? 0.18 : 0;
    for (let y = BAND + 3; y <= BB0; y++) body(x, y, 0.16 + Math.max(0, dx) * 0.0035 + edge - (y - BAND) * 0.004);
    for (let y = rakeRow(x, B0, HB, BAND - 1); y < BAND; y++) body(x, y, 0.2 + Math.max(0, dx) * 0.005 + edge);
    put(x, BAND, TWT[0]); put(x, BAND + 1, TWT[2]); put(x, BAND + 2, TY[5]);                             // string course
  }
  cornice(119, 25, BB0, true);                                                 // the lower gable
  cornice(B0, HB, BAND - 1, true);                                             // the pediment's raking cornice
  for (let x = X - 21; x <= X + 21; x++) put(x, rakeRow(x, B0 + 4, 21, BAND - 2), x <= X ? TWT[2] : TWT[3]);   // the tympanum's inner line
  for (let x = X - 21; x <= X + 21; x++) put(x, BAND - 2, TWT[3]);
  put(X - HB - 1, BAND, TWT[1]); put(X + HB + 1, BAND, TWT[3]);
  for (let y = BAND - 4; y < BAND; y++) { put(X - HB, y, TWT[0]); put(X - HB + 1, y, TWT[1]); put(X + HB - 1, y, TWT[3]); put(X + HB, y, TWT[4]); }   // acroteria
  for (let x = X - 3; x <= X + 3; x++) { put(x, 102, '#eadfc6'); put(x, 103, '#c8b898'); }                // the dedication plaque
  for (let y = 111; y <= 118; y++) for (let x = X - 3; x <= X + 3; x++) {                                  // the arched window
    const d = Math.abs(x - X), top = y === 111, inner = d <= 2 && y > 111 && y < 118 && !(y === 112 && d === 2);
    if (top && d > 1) continue;
    if (y === 112 && d === 3) continue;
    put(x, y, inner ? (y < 114 ? '#5a4240' : '#3a2a2e') : x < X || top ? TWT[1] : TWT[3]);
  }
  // --- the clock stage (rows 66-96, 37 wide), its foot set back behind the pediment
  const HC = 18;
  for (let x = X - HC; x <= X + HC; x++) for (let y = TR.clkBot + 1; y < rakeRow(x, B0, HB, BAND - 1); y++) body(x, y, 0.48 + (x > X ? 0.12 : 0));
  for (let x = X - HC - 1; x <= X + HC + 1; x++) { put(x, 93, x < X ? TWT[1] : TWT[2]); body(x, 94, 0.28); body(x, 95, 0.4); body(x, 96, 0.52); }   // plinth
  const louvre = (c, x, y) => {                                                // 1 inside / 2 frame of an arched louvre centred on c
    const d = Math.abs(x - c);
    if (y < 71 || y > 90) return 0;
    if (y === 90) return d <= 3 ? 2 : 0;
    if (y === 71) return d <= 1 ? 2 : 0;
    if (y === 72) return d <= 1 ? 1 : d === 2 ? 2 : 0;
    return d <= 2 ? 1 : d === 3 ? 2 : 0;
  };
  for (let y = 69; y < 93; y++) for (let x = X - HC; x <= X + HC; x++) {
    const dx = x - X;
    if (dx <= -HC + 1) { put(x, y, dx === -HC ? TWT[0] : TWT[1]); continue; }
    if (dx >= HC - 1) { put(x, y, dx === HC - 1 ? TWT[2] : TWT[3]); continue; }
    const L = louvre(X - 11, x, y), R = louvre(X + 11, x, y);
    if (L === 1) put(x, y, (y & 1) ? '#4a3e44' : '#8c7e72');
    else if (R === 1) put(x, y, (y & 1) ? '#3e343a' : '#76685e');
    else if (L === 2) put(x, y, TWT[1]);
    else if (R === 2) put(x, y, TWT[2]);
    else body(x, y, 0.14 + Math.max(0, dx) * 0.007);
  }
  for (let dy = -7; dy <= 7; dy++) for (let dx = -7; dx <= 7; dx++) {          // the dial (hands drawn per frame)
    const d = Math.hypot(dx, dy);
    if (d > 6.6) continue;
    put(X + dx, TR.dial + dy, d > 5.6 ? '#3a2c26' : dx + dy > 4 ? '#f2ead6' : '#fffaee');
  }
  for (let h = 0; h < 12; h++) {
    const a = h / 12 * TAU;
    put(X + Math.round(Math.sin(a) * 4.6), TR.dial - Math.round(Math.cos(a) * 4.6), h % 3 === 0 ? '#2a2026' : '#b8ac98');
  }
  for (let x = X - HC - 2; x <= X + HC + 2; x++) { put(x, TR.clkTop, x < X ? TWT[0] : TWT[1]); put(x, TR.clkTop + 1, x < X ? TWT[1] : TWT[2]); put(x, TR.clkTop + 2, TY[5]); }
  for (const sx of [-1, 1]) {                                                  // corner pinnacles on the cornice
    const cx = X + sx * (HC + 1);
    for (let y = TR.clkTop - 4; y < TR.clkTop; y++) for (let k = -1; k <= 1; k++) put(cx + k, y, k < 0 ? TWT[1] : k > 0 ? TWT[3] : TWT[2]);
    put(cx, TR.clkTop - 5, TWT[sx < 0 ? 0 : 2]); put(cx, TR.clkTop - 6, TWT[sx < 0 ? 1 : 3]); put(cx, TR.clkTop - 7, TWT[2]);
  }
  // --- the octagonal belfry (rows 45-65, 25 wide): three faces, pointed louvred windows, a white cornice, small pinnacles
  const HU = 12, win = (x, y) => {
    const d = Math.abs(x - X);
    if (y >= 49 && y <= 58 && d <= Math.min(2, y - 49)) return true;
    for (const c of [X - 10, X + 10]) if (y >= 50 && y <= 58 && Math.abs(x - c) <= (y > 51 ? 1 : 0)) return true;
    return false;
  };
  for (let y = TR.upTop + 2; y < TR.clkTop; y++) for (let x = X - HU; x <= X + HU; x++) {
    const dx = x - X, face = dx < -7 ? 0.3 : dx > 7 ? 0.56 : 0.12;
    if (win(x, y)) { put(x, y, (y & 1) ? (dx > 7 ? '#3a3036' : '#4a3e44') : (dx > 7 ? '#6e6258' : '#8c7e72')); continue; }
    if (win(x - 1, y) || win(x + 1, y) || win(x, y - 1) || win(x, y + 1)) { put(x, y, dx > 7 ? TWT[3] : TWT[1]); continue; }
    if (dx === -HU) put(x, y, TWT[1]); else if (dx === HU) put(x, y, TY[6]);
    else if (dx === -7) put(x, y, TWT[1]); else if (dx === 7) put(x, y, TWT[3]);
    else body(x, y, face + (y > 61 ? 0.06 : 0));
  }
  for (let x = X - HU - 1; x <= X + HU + 1; x++) { put(x, TR.upTop, x < X ? TWT[0] : TWT[2]); put(x, TR.upTop + 1, TY[5]); }
  for (const cx of [X - HU - 1, X + HU + 1]) { for (let y = TR.upTop - 4; y < TR.upTop; y++) put(cx, y, TWT[cx < X ? 1 : 3]); put(cx, TR.upTop - 5, TWT[2]); }
  // the little pediment over the dial, standing on the clock stage's cornice
  for (let x = X - 12; x <= X + 12; x++) {
    const r = rakeRow(x, 59, 12, TR.clkTop - 1);
    for (let y = r; y < TR.clkTop; y++) put(x, y, y === r ? (x <= X ? TWT[0] : TWT[2]) : y === r + 1 ? (x <= X ? TWT[1] : TWT[3]) : pick(TY, 0.18 + Math.max(0, x - X) * 0.01, x, y));
  }
  // --- the spire (rows 13-44): slender, octagonal, white ribs on its edges, lucarnes, gablets and pinnacles round its foot
  for (let y = TR.spire; y < TR.upTop; y++) {
    const hw = Math.round((y - TR.spire) * 6.5 / (TR.upTop - TR.spire)), rib = Math.round(hw * 0.45);
    for (let dx = -hw; dx <= hw; dx++) {
      const x = X + dx;
      if (dx === -hw && hw > 0) put(x, y, TWT[2]);
      else if (dx === hw && hw > 0) put(x, y, TY[6]);
      else if (hw > 1 && (dx === -rib || dx === rib)) put(x, y, dx < 0 ? TWT[1] : TWT[3]);
      else body(x, y, dx < -rib ? 0.32 : dx > rib ? 0.6 : 0.12);
    }
  }
  put(X, 26, TWT[0]); put(X, 27, '#4a3a3c'); put(X, 28, '#3a2c30');                                       // lucarnes
  for (const sx of [-3, 3]) { put(X + sx, 33, sx < 0 ? TWT[1] : TWT[3]); put(X + sx, 34, '#3a2c30'); put(X + sx, 35, '#3a2c30'); }
  const gab = (cx, apex, hw, base, shade) => {
    for (let x = cx - hw; x <= cx + hw; x++) {
      const r = apex + Math.round(Math.abs(x - cx) * (base - apex - 1) / hw);
      for (let y = r; y <= base; y++) put(x, y, y === r ? (x <= cx ? TWT[0] : TWT[2]) : pick(TY, shade + (x > cx ? 0.1 : 0), x, y));
    }
  };
  gab(X - 10, 40, 2, 44, 0.34); gab(X + 10, 40, 2, 44, 0.62); gab(X, TR.gab, 6, 44, 0.12);
  put(X, 40, '#4a3a3c'); put(X, 41, '#3a2c30'); put(X, 42, '#3a2c30');
  for (const cx of [X - 7, X + 7]) { for (let y = 36; y < 45; y++) put(cx, y, cx < X ? TWT[1] : TWT[3]); put(cx, 35, TWT[2]); put(cx, 34, TWT[3]); }
  // collar, orb and the weathervane
  put(X - 1, 12, TWT[1]); put(X, 12, TWT[1]); put(X + 1, 12, TWT[3]); put(X, 11, '#a88a58');
  put(X, TR.orb, '#ffeebc'); put(X - 1, TR.orb + 1, '#ffe8a8'); put(X, TR.orb + 1, '#fff2c8'); put(X + 1, TR.orb + 1, '#c89a58'); put(X, TR.orb + 2, '#c89a58');
  for (let y = TR.vane + 2; y < TR.orb; y++) put(X, y, '#8a6a3e');                                        // rod, then the arrow
  for (let x = X - 4; x <= X + 3; x++) put(x, TR.vane + 2, x < X - 2 ? '#6a4e30' : '#a8864e');
  put(X - 4, TR.vane + 1, '#6a4e30'); put(X - 3, TR.vane + 1, '#6a4e30'); put(X - 4, TR.vane + 3, '#6a4e30'); put(X - 3, TR.vane + 3, '#6a4e30');
  put(X + 2, TR.vane + 1, '#a8864e'); put(X + 2, TR.vane + 3, '#a8864e'); put(X + 1, TR.vane, '#a8864e');
}

/* ================= Pedro de Heredia: the founder in bronze on a granite pedestal, a dark figure against the lit wall ================= */
// Pedestal after the photos: a broad round step people sit on, a base block, the shaft with the bronze coat of arms, a
// cornice; the figure in a cloak, a rolled document held to his chest. Bronze lit from below by the plaza's lamps.
const STONE = ['#c8b8a0', '#ac9c86', '#8e806e', '#706458', '#544a44', '#3a3432'];
const BRONZE = ['#7a6448', '#5a4a38', '#3e342a', '#2c241e', '#1e1816'];
function renderStatue(p) {
  const { put } = p, X = STATUE.x, B = STATUE.base;
  const stone = (x, y, v) => put(x, y, pick(STONE, v, x, y));
  for (let y = B - 2; y <= B; y++) {                                           // the round step
    const hw = y === B - 2 ? 14 : 18;
    for (let dx = -hw; dx <= hw; dx++) stone(X + dx, y, y === B - 2 ? 0.1 + Math.abs(dx) / 60 : 0.35 + (y - B + 1) * 0.15 + Math.max(0, dx) / 50);
  }
  for (let y = B - 8; y < B - 2; y++) for (let dx = -9; dx <= 9; dx++)           // base block
    stone(X + dx, y, y === B - 8 ? 0.08 : 0.3 + (dx > 6 ? 0.3 : 0) + (y - B + 8) * 0.03);
  for (let y = B - 28; y < B - 8; y++) for (let dx = -7; dx <= 7; dx++) {        // the shaft
    const plaque = Math.abs(dx) <= 3 && y >= B - 23 && y <= B - 16;
    if (plaque) { const rim = Math.abs(dx) === 3 || y === B - 23 || y === B - 16; put(X + dx, y, rim ? BRONZE[1] : ((dx + y) & 1) ? BRONZE[3] : BRONZE[2]); continue; }
    stone(X + dx, y, 0.32 + (dx > 4 ? 0.28 : dx < -5 ? -0.1 : 0) + (B - 8 - y) * 0.008 + (y === B - 12 ? 0.25 : 0));
  }
  for (let dx = -8; dx <= 8; dx++) { stone(X + dx, B - 31, 0.1); stone(X + dx, B - 30, 0.22); stone(X + dx, B - 29, 0.55); }   // cornice
  // the figure: boots, the cloak's long fall, shoulders, the head turned a little to our left, the document
  const F = B - 32;                                                            // the figure's foot row
  const rows = [                                                               // [row offset above F, x0, x1] of the silhouette
    [0, -5, 5], [1, -3, 3], [2, -3, 3], [3, -4, 4], [4, -4, 4], [5, -4, 5], [6, -4, 5], [7, -4, 4], [8, -4, 4], [9, -4, 4],
    [10, -4, 4], [11, -4, 4], [12, -4, 4], [13, -4, 4], [14, -4, 4], [15, -5, 4], [16, -4, 3], [17, -1, 1], [18, -2, 1],
    [19, -2, 1], [20, -2, 1], [21, -1, 0],
  ];
  for (const [j, x0, x1] of rows) for (let dx = x0; dx <= x1; dx++) {
    const y = F - j, rim = dx === x0 || (j < 3 && dx < 0);
    put(X + dx, y, j === 0 ? BRONZE[2] : rim ? BRONZE[0] : pick(BRONZE.slice(1), 0.3 + (dx - x0) / (x1 - x0 + 1) * 0.6, X + dx, y));
  }
  for (let j = 4; j < 15; j += 3) put(X - 1 + (j & 1), F - j, BRONZE[3]);       // folds in the cloak
  for (let j = 9; j <= 13; j++) put(X + 5, F - j, j === 13 ? BRONZE[0] : BRONZE[1]);   // the arm and the rolled document
  put(X + 6, F - 13, '#8a7458'); put(X + 6, F - 12, BRONZE[1]); put(X + 6, F - 14, '#8a7458');
  put(X - 2, F - 20, BRONZE[0]); put(X - 2, F - 19, BRONZE[1]);                   // a lit cheek
}

/* ================= The Portal de los Dulces: an arcade of sweet stalls under a long wooden balcony ================= */
// A coral house of two storeys with a tiled roof and a long dark-wood balcony on turned balusters, and the taller cream
// house beyond it with a balustraded parapet and a stone balcony; the arcade of round arches runs under both. The
// bougainvillea climbs a pier and spills over the balcony rail (the falling petals come from it).
const CORAL_R = ['#f8b49a', '#ec9a80', '#d8806a', '#b86656', '#8e4e46', '#663a38', '#442a2c'];
const CRM = ['#fff4dc', '#f2e2c0', '#dcc8a0', '#bca682', '#94806a', '#6a5a50', '#463c3a'];
const WOOD = ['#8a5a3a', '#6a4430', '#4a2e22', '#331e18', '#221410'];
const TILE = ['#c8704a', '#a8563a', '#86422e', '#663226', '#4a2420'];
const BOUG = [];                                                               // bougainvillea clusters: { x, y, r, ph }
{
  const r = rng(808);
  for (let i = 0; i < 34; i++) {
    const a = r(), x = i < 14 ? 412 + r() * 6 : 398 + r() * 40, y = i < 14 ? 150 + r() * 36 : 138 + r() * 16;
    BOUG.push({ x: Math.round(x), y: Math.round(y), r: 1.6 + r() * 2.2, ph: a });
  }
}
function arcadeOpen(x, y) {                                                    // inside one of the arcade's arches
  if (y < CORAL.beam + 3 || y >= PORTAL.floor) return false;
  for (let i = 0; i < PIERS.length; i++) {
    const a = PIERS[i] + 4, b = (PIERS[i + 1] ?? PORTAL.x1 + 30) - 1;
    if (x < a || x > b) continue;
    const cx = (a + b) / 2, rx = (b - a + 1) / 2;
    return y >= CORAL.spring || ((x - cx) / rx) ** 2 + ((y - CORAL.spring) / 10) ** 2 <= 1;
  }
  return false;
}
function renderPortal() {
  const p = painter(W, H), { put } = p;
  const lightUp = y => clamp((y - 110) / 95, 0, 1);                           // the arcade's light fades up the front
  for (let x = PORTAL.x0; x < PORTAL.x1; x++) {
    const coral = x < CORAL.x1, top = coral ? CORAL.eave + 8 : CREAM.top + 10, ramp = coral ? CORAL_R : CRM;
    for (let y = top; y < PORTAL.floor; y++) {
      if (arcadeOpen(x, y)) {                                                  // the arcade's lamplit back wall and shop doors
        const door = ((x - 359) % 30 + 30) % 30 < 12 && y > 182, v = clamp((y - CORAL.beam) / 50, 0, 1);
        put(x, y, door ? pick(['#ffd890', '#e8a860', '#b07440'], 0.2 + (1 - v) * 0.4 + ((x - 359) % 30 === 0 ? 0.6 : 0), x, y)
          : pick(['#f2b878', '#d8985c', '#b07446', '#7a4e34'], 0.15 + (1 - v) * 0.7, x, y));
        continue;
      }
      put(x, y, pick(ramp, 0.62 - lightUp(y) * 0.42 + (x === PORTAL.x0 ? -0.2 : 0) + hash(x >> 1, y >> 2, 301) * 0.08, x, y));
    }
  }
  // arcade: the piers' lit faces, the arches' rims, the beam under the balcony, the floor's step
  for (const pr of PIERS) for (let y = CORAL.beam + 2; y < PORTAL.floor; y++) for (let k = 0; k < 4; k++) {
    const ramp = pr + k < CORAL.x1 ? CORAL_R : CRM;
    put(pr + k, y, y > PORTAL.floor - 4 ? '#8a6a5a' : pick(ramp, 0.08 + k * 0.14 + (y < CORAL.spring ? 0.1 : 0), pr + k, y));
  }
  for (let x = PORTAL.x0; x < PORTAL.x1; x++) if (!arcadeOpen(x, CORAL.spring - 1)) for (let y = CORAL.beam + 3; y < CORAL.spring; y++)
    if (arcadeOpen(x, y + 1) && !arcadeOpen(x, y)) put(x, y, x < CORAL.x1 ? '#ffd0b4' : '#fff4dc');
  for (let x = PORTAL.x0; x < PORTAL.x1; x++) {
    put(x, CORAL.beam, x < CORAL.x1 ? '#f0a88c' : '#f2e2c0'); put(x, CORAL.beam + 1, x < CORAL.x1 ? '#b86656' : '#bca682'); put(x, CORAL.beam + 2, '#5a3a32');
    put(x, PORTAL.floor, '#c8a07a'); put(x, PORTAL.floor + 1, '#6a4a38'); put(x, PORTAL.floor + 2, '#3a2a26');
  }
  // the coral house's tiled roof and eave, the eave running on as an awning over the balcony in front of the cream house
  for (let x = PORTAL.x0 - 2; x < PORTAL.x1; x++) {
    const roofTop = x < CORAL.x1 ? CORAL.eave - 6 + Math.round(Math.abs(x - 396) / 30) : CORAL.eave + 2;
    for (let y = roofTop; y <= CORAL.eave + 5; y++) {
      const lip = y >= CORAL.eave + 4, row = (y - roofTop) % 3 === 2;
      put(x, y, lip ? ((x >> 1) & 1 ? TILE[1] : TILE[3]) : row ? TILE[4] : pick(TILE, 0.3 + (y - roofTop) * 0.04 + ((x >> 1) & 1) * 0.15, x, y));
    }
    put(x, CORAL.eave + 6, (x % 5 === 0) ? '#a8704a' : '#2a1a16'); put(x, CORAL.eave + 7, '#1e1414');            // rafter ends
  }
  // the cream house above the awning: balustraded parapet, cornice, a tall door onto a stone balcony
  for (let x = CREAM.x0; x < PORTAL.x1; x++) {
    put(x, CREAM.top, CRM[1]); put(x, CREAM.top + 1, CRM[3]);
    for (let y = CREAM.top + 2; y < CREAM.top + 7; y++) put(x, y, (x - CREAM.x0) % 10 < 2 ? pick(CRM, 0.25, x, y) : (x & 1) ? pick(CRM, 0.3 + (y === CREAM.top + 2 ? 0.2 : 0), x, y) : null);
    put(x, CREAM.top + 7, CRM[2]); put(x, CREAM.top + 8, CRM[0]); put(x, CREAM.top + 9, '#5a4a44');
  }
  for (const fx of [CREAM.x0 + 1, CREAM.x0 + 21]) { put(fx, CREAM.top - 1, CRM[1]); put(fx, CREAM.top - 2, CRM[2]); }
  for (let y = 94; y < 120; y++) for (let x = 453; x < 464; x++) {
    const fr = x === 453 || x === 463 || y === 94;
    put(x, y, fr ? CRM[0] : x === 458 ? '#1e3a30' : y > 104 && x > 458 ? '#f2c47a' : ((y - 95) % 3 === 0 ? '#2a4a3e' : '#3a6450'));
  }
  for (let x = 450; x < 467; x++) { put(x, 120, CRM[0]); put(x, 121, CRM[4]); for (let y = 113; y < 120; y++) if ((x & 1) && y > 113) put(x, y, CRM[2]); put(x, 113, CRM[1]); }
  // doors behind the long balcony: wooden shutters, one open onto a lamplit room
  for (const [x0, open] of [[362, false], [389, true], [416, false], [452, false]]) for (let y = CORAL.eave + 10; y < CORAL.deck; y++) for (let x = x0; x < x0 + 10; x++) {
    const fr = x === x0 || x === x0 + 9 || y === CORAL.eave + 10;
    put(x, y, fr ? (x0 > 440 ? CRM[1] : '#ffc8ae') : open ? pick(['#ffe2a8', '#f6c47a', '#d0905a', '#9a6040'], 0.1 + Math.abs(x - x0 - 4.5) / 9 + (y - CORAL.eave) / 120, x, y)
      : x === x0 + 4 || x === x0 + 5 ? WOOD[3] : ((y - CORAL.eave) % 3 === 0 ? WOOD[2] : WOOD[1]));
  }
  // the long wooden balcony: posts to the eave, turned balusters, the handrail, the deck and its corbels
  for (const px0 of [353, 381, 410, 438, 466]) for (let y = CORAL.eave + 8; y < CORAL.deck; y++) { put(px0, y, WOOD[1]); put(px0 + 1, y, WOOD[3]); }
  for (let x = PORTAL.x0; x < PORTAL.x1; x++) {
    put(x, CORAL.rail, WOOD[0]); put(x, CORAL.rail + 1, WOOD[2]);
    for (let y = CORAL.rail + 2; y < CORAL.deck; y++) if ((x & 1) === 0) put(x, y, (y - CORAL.rail) % 4 === 1 ? WOOD[0] : WOOD[2]);
    put(x, CORAL.deck, WOOD[0]); put(x, CORAL.deck + 1, WOOD[2]); put(x, CORAL.deck + 2, WOOD[4]);
    if (x % 8 === 2) for (let k = 0; k < 4; k++) { put(x, CORAL.deck + 3 + k, WOOD[2]); if (k < 2) put(x + 1, CORAL.deck + 3 + k, WOOD[3]); }
  }
  // bougainvillea up the pier and over the rail: dark leaves, magenta bracts
  for (let y = 140; y < PORTAL.floor - 2; y++) if (hash(y, 1, 303) < 0.7) put(414 + Math.round(Math.sin(y * 0.3)), y, '#3a2a1e');
  for (const b of BOUG) for (let dy = -3; dy <= 3; dy++) for (let dx = -3; dx <= 3; dx++) {
    const d = Math.hypot(dx, dy * 1.2);
    if (d > b.r + 0.6) continue;
    const h = hash(b.x + dx, b.y + dy, 304);
    put(b.x + dx, b.y + dy, h < 0.38 ? (dy < 0 ? '#2e5a32' : '#1e3a22') : h < 0.62 ? '#b5179e' : h < 0.86 ? '#d6247e' : '#ff6ab8');
  }
  return p.done();
}

/* ================= The Portal's north bay, close on the right: a blue house over a big arch and a sweet stall ================= */
// The arcade of the Portal de los Dulces starts right beside us at the plaza's north end and recedes to the far corner;
// its nearest bay fills the right edge: a pier, a big round arch, the stall of cocadas under a bare bulb, the long
// wooden balcony above with the bougainvillea, the tiled eave. The lit stall is the scene's warm near corner.
const BLUE = ['#94bcec', '#7aa2d8', '#5e86c0', '#486ca4', '#365484', '#283e64', '#1c2c48'];
const N_ARCH = { cx: 446.5, r: 36.5, x0: 410 };
const N_BULB = [444, 160];
const nearArch = (x, y) => y < NEAR.floor && x >= N_ARCH.x0 && (y >= NEAR.spring || Math.hypot(x - N_ARCH.cx, y - NEAR.spring) <= N_ARCH.r);
const NBOUG = [];
{
  const r = rng(909);
  for (let i = 0; i < 46; i++) {
    const hang = i < 18, x = hang ? 400 + r() * 12 : 400 + r() * 44, y = hang ? 108 + r() * 64 : 84 + r() * 24;
    NBOUG.push({ x: Math.round(x), y: Math.round(y), r: 2.2 + r() * 2.6 });
  }
}
function renderNear() {
  const p = painter(W, H), { put } = p, N = NEAR;
  for (let x = N.x0; x < W; x++) for (let y = N.eave + 2; y < N.floor; y++) {
    if (nearArch(x, y)) {                                                      // under the arch: the lamplit shop behind the stall
      const back = x > 460 && y > 158 ? 0.05 : 0.15;
      const v = clamp(0.1 + Math.hypot((x - N_BULB[0]) / 60, (y - N_BULB[1] - 10) / 46) * 0.7 + back, 0, 1);
      put(x, y, pick(['#ffe2a0', '#f4bc78', '#d8985c', '#b07446', '#7a4e34', '#4a3028'], v, x, y));
      continue;
    }
    const d = Math.hypot(x - N_ARCH.cx, y - N.spring);
    if (x >= N_ARCH.x0 - 3 && y < N.spring && d <= N_ARCH.r + 3) {             // the voussoirs, white-washed
      const a = Math.atan2(y - N.spring, x - N_ARCH.cx), joint = Math.abs(frac(a / TAU * 36) - 0.5) > 0.44;
      put(x, y, joint ? '#8a96a8' : d > N_ARCH.r + 2 ? '#c8d0dc' : x < N_ARCH.cx ? '#f2f4f6' : '#dce2ea');
      continue;
    }
    const lit = clamp((y - 70) / 140, 0, 1);
    put(x, y, pick(BLUE, 0.6 - lit * 0.34 + (x <= N.x0 + 1 ? -0.2 : 0) + hash(x >> 1, y >> 2, 901) * 0.08 + (hash(x >> 2, y >> 3, 902) < 0.08 ? 0.12 : 0), x, y));
  }
  // the pier: its lit face, an impost moulding, a stone plinth
  for (let y = N.spring - 4; y < N.floor; y++) for (let x = N.x0; x < N_ARCH.x0; x++) {
    const k = x - N.x0;
    put(x, y, y > N.floor - 9 ? pick(['#b8a48a', '#8a7a68', '#5e5248'], 0.2 + k / 14, x, y) : pick(BLUE, 0.18 + k * 0.035 + (k === 0 ? -0.15 : 0), x, y));
  }
  for (let x = N.x0 - 2; x < N_ARCH.x0 + 2; x++) { put(x, N.spring - 6, '#f4f6f8'); put(x, N.spring - 5, '#dce2ea'); put(x, N.spring - 4, '#7a889c'); }
  // shelves of jars on the shop's back wall, and its lit doorway
  for (const sy of [166, 176]) for (let x = 414; x < 458; x++) {
    put(x, sy, '#5a3a26');
    if (hash(x, sy, 903) < 0.7) put(x, sy - 1, ['#f4ecd8', '#e86a9a', '#d8a050', '#b8865a', '#8a4a2a'][Math.floor(hash(x, sy, 904) * 5)]);
    if (hash(x, sy, 905) < 0.4) put(x, sy - 2, '#e8f0f2');
  }
  for (let y = 160; y < N.floor; y++) for (let x = 464; x < 478; x++) put(x, y, x === 464 || y === 160 ? '#6a4430' : pick(['#fff0c0', '#ffd890', '#e8b070'], 0.2 + (y - 160) / 80, x, y));
  // the upper floor: tall doors (one open on a lamplit room with a lace curtain), the long wooden balcony, the posts
  for (const [x0, open] of [[416, true], [452, false]]) for (let y = 58; y < N.deck; y++) for (let x = x0; x < x0 + 18; x++) {
    const fr = x === x0 || x === x0 + 17 || y === 58;
    put(x, y, fr ? '#eef2f6' : open ? (x < x0 + 4 ? '#fff4e4' : pick(['#ffe2a8', '#f6c47a', '#d0905a', '#9a6040'], 0.1 + Math.abs(x - x0 - 9) / 14 + (y - 58) / 120, x, y))
      : x === x0 + 8 || x === x0 + 9 ? WOOD[3] : ((y - 58) % 3 === 0 ? '#1e3a2e' : '#2e5444'));
  }
  for (const px0 of [400, 440, 478]) for (let y = N.eave + 2; y < N.deck; y++) { put(px0, y, WOOD[1]); put(px0 + 1, y, WOOD[3]); }
  for (let x = N.x0; x < W; x++) {
    put(x, N.rail, WOOD[0]); put(x, N.rail + 1, WOOD[1]); put(x, N.rail + 2, WOOD[3]);
    const b = (x - N.x0) % 4;
    for (let y = N.rail + 3; y < N.deck; y++) if (b === 1 || b === 2) put(x, y, (y - N.rail) % 7 === 4 ? WOOD[0] : b === 1 ? WOOD[1] : WOOD[2]);
    put(x, N.deck, WOOD[0]); put(x, N.deck + 1, WOOD[1]); put(x, N.deck + 2, WOOD[3]); put(x, N.deck + 3, WOOD[4]);
    if ((x - N.x0) % 10 === 4) for (let k = 0; k < 6; k++) for (let q = 0; q < Math.max(1, 3 - (k >> 1)); q++) put(x + q, N.deck + 4 + k, q ? WOOD[3] : WOOD[1]);
  }
  // the tiled eave and its rafters
  for (let x = N.x0 - 2; x < W; x++) {
    const top = N.eave - 12 + Math.round((x - N.x0) / 40);
    for (let y = top; y <= N.eave + 1; y++) {
      const lip = y >= N.eave, row = (y - top) % 3 === 2;
      put(x, y, lip ? ((x >> 1) & 1 ? TILE[1] : TILE[3]) : row ? TILE[4] : pick(TILE, 0.3 + (y - top) * 0.03 + ((x >> 1) & 1) * 0.15, x, y));
    }
    put(x, N.eave + 2, (x % 6 === 0) ? '#a8704a' : '#2a1a16'); put(x, N.eave + 3, '#1e1414');
  }
  // the floor of the arcade and its step down to the plaza
  for (let x = N.x0 - 2; x < W; x++) { put(x, N.floor, '#d8b088'); put(x, N.floor + 1, '#a88466'); put(x, N.floor + 2, '#6a4a3a'); put(x, N.floor + 3, '#4a3430'); put(x, N.floor + 4, '#2a1e20'); }
  // bougainvillea over the rail and down the pier
  for (let y = 100; y < 180; y++) if (hash(y, 2, 906) < 0.75) put(402 + Math.round(Math.sin(y * 0.25) * 1.5), y, '#3a2a1e');
  for (const b of NBOUG) for (let dy = -5; dy <= 5; dy++) for (let dx = -5; dx <= 5; dx++) {
    if (Math.hypot(dx, dy * 1.15) > b.r + 0.6 + (hash(b.x + dx, b.y + dy, 907) - 0.5)) continue;
    const h = hash(b.x + dx, b.y + dy, 908);
    put(b.x + dx, b.y + dy, h < 0.34 ? (dy < 0 ? '#2e5a32' : '#1e3a22') : h < 0.6 ? '#b5179e' : h < 0.86 ? '#d6247e' : '#ff6ab8');
  }
  return p.done();
}
// the stall in front of its seller: a cloth-covered table, big glass jars of cocadas, alegrías and panelitas, a tray
function renderNearStall() {
  const p = painter(W, H), { put } = p;
  for (let x = 416; x <= 471; x++) {
    put(x, 190, '#fff6e6'); put(x, 191, '#e0d0b4');
    for (let y = 192; y < NEAR.floor; y++) put(x, y, y === 193 || y === 203 ? '#c8303a' : (x - 416) % 7 === 0 ? '#d8c8aa' : '#f0e2c6');
  }
  signText(put, 'COCADAS', 430, 196, '#b8202c');
  const J = [['#f4ecd8', '#d8ccb4'], ['#e86a9a', '#b04070'], ['#d8a050', '#a8743a'], ['#8a4a2a', '#5e301c'], ['#f2d0a0', '#c8a478'], ['#7a3a5a', '#4e2238']];
  [418, 425, 432, 451, 458, 465].forEach((jx, i) => {
    const [a, b] = J[i];
    for (let x = jx; x < jx + 6; x++) { put(x, 179, '#e8c060'); put(x, 180, x === jx + 5 ? '#a8803a' : '#d8b050'); }
    for (let y = 181; y < 190; y++) for (let x = jx; x < jx + 6; x++) put(x, y, x === jx ? '#eef6f8' : x === jx + 5 ? '#b8c8cc' : y < 183 ? '#dde8ea' : (x + y) % 5 === 0 ? b : a);
  });
  for (let x = 439; x < 450; x++) { put(x, 189, '#8a6a4a'); if (x % 3 !== 2) { put(x, 188, '#f4ecd8'); put(x, 187, x % 3 ? '#e8dcc0' : '#fffaf0'); } }
  return p.done();
}

/* ================= The plaza: wet brick in perspective, pale stone bands, the lamps' pools ================= */
const EYE = 175, VX = TX;                                                       // eye level and the vanishing column
const BRICK = ['#8a5640', '#744634', '#5e382c', '#4a2c24', '#3a221e', '#2a1818'];
function renderGround() {
  const p = painter(W, H), { put } = p;
  for (let x = 0; x < W; x++) {
    const y0 = x < PORTAL.x0 ? wallFoot(x) : x < NEAR.x0 ? PORTAL.floor + 3 : NEAR.floor + 5;
    for (let y = y0; y < H; y++) {
      const z = 486 / (y - EYE), z1 = 486 / (y + 1 - EYE), u = (x - VX) * z / 140, u1 = (x + 1 - VX) * z / 140;
      const course = Math.floor(z * 2) !== Math.floor(z1 * 2) && z < 26;       // brick courses, only where they're 2 px apart
      const band = Math.floor(z / 7) !== Math.floor(z1 / 7) || Math.floor(u / 5) !== Math.floor(u1 / 5);
      const brickJ = !course && z < 14 && Math.floor(u * 4 + (Math.floor(z * 2) & 1) * 0.5) !== Math.floor(u1 * 4 + (Math.floor(z * 2) & 1) * 0.5);
      let v = 0.42 + clamp((y - 186) / 140, 0, 0.3) + hash(Math.floor(u * 4), Math.floor(z * 2), 401) * 0.12;
      for (const L of LAMPS) v -= Math.max(0, 1 - Math.hypot((x - L.x) / 46, (y - L.base) / 16)) * 0.3;
      v -= Math.max(0, 1 - Math.hypot((x - 400) / 90, (y - PORTAL.floor) / 26)) * 0.26;                    // the arcade's spill
      v -= Math.max(0, 1 - Math.hypot((x - 222) / 18, (y - 199) / 8)) * 0.18;                            // the raspao cart's bulb
      if (y < y0 + 3 && x < PORTAL.x0) v += 0.1;                                                          // the wall's damp shadow
      put(x, y, band ? pick(['#946a52', '#7a5644', '#624538', '#4a362c'], v * 0.9, x, y)
        : course || brickJ ? pick(BRICK.slice(2), v, x, y) : pick(BRICK, v - 0.08, x, y));
    }
  }
  // stone balls on short plinths along the arcade's edge (the Portal's bollards), close to us at the bottom right
  for (const [bx, by, r] of [[372, 228, 4], [456, 240, 7]]) {
    for (let x = bx - r - 1; x <= bx + r + 1; x++) for (let k = 0; k < 2; k++) put(x, by + r + 1 + k, '#140e10');
    for (let dy = -r; dy <= r; dy++) for (let dx = -r; dx <= r; dx++) {
      const d = Math.hypot(dx, dy);
      if (d > r + 0.4) continue;
      put(bx + dx, by + dy, pick(['#9a8a7a', '#6e6058', '#4a4040', '#2e2a2c', '#1e1a1c'], 0.15 + (dx + dy + r) / (2 * r) * 0.5 + (dy > 0 ? 0.25 : 0), bx + dx, by + dy));
    }
  }
  return p.done();
}

// puddles left by the shower: clearer mirrors in the wet brick (rows of [x0, x1] spans), drawn darker, then mirrored again
const ZONES = [                                                                // reflection bands: the plaza, the far arcade, the near bay
  { x: 0, w: PORTAL.x0, top: WALL_FOOT + 1, sq: 1.05 }, { x: PORTAL.x0, w: NEAR.x0 - PORTAL.x0, top: PORTAL.floor + 3, sq: 1 },
  { x: NEAR.x0, w: W - NEAR.x0, top: NEAR.floor + 5, sq: 1 },
];
const PUDDLES = [[204, 252, 56, 9], [146, 223, 22, 3], [266, 219, 24, 3], [334, 238, 36, 5], [440, 226, 30, 4]].map(([cx, cy, rx, ry]) => ({
  cx, cy, zone: cx < PORTAL.x0 ? 0 : cx < NEAR.x0 ? 1 : 2, rows: Array.from({ length: 2 * ry + 1 }, (_, k) => { const dy = k - ry, hw = Math.round(rx * Math.sqrt(1 - (dy / (ry + 0.5)) ** 2)); return [cy + dy, cx - hw, cx + hw]; }),
}));
function puddleClip(zone) {
  ctx.beginPath();
  for (const q of PUDDLES) if (q.zone === zone) for (const [y, x0, x1] of q.rows) ctx.rect(x0, y, x1 - x0 + 1, 1);
  ctx.clip();
}

/* ================= Five-globe cast-iron lamps on the plaza (globes lit per frame) ================= */
const lampTop = L => L.base - L.h, lampS = L => Math.max(4, Math.round(L.h * 0.075));
function renderLamps() {
  const p = painter(W, H), { put } = p, IR = ['#4a4048', '#2a2228', '#18121a'];
  for (const L of LAMPS) {
    const top = lampTop(L), s = lampS(L), big = L.h > 80;
    for (let y = L.base - 7; y <= L.base; y++) { const hw = y > L.base - 2 ? 3 : 2; for (let k = -hw; k <= hw; k++) put(L.x + k, y, IR[k < 0 ? 0 : k > 0 ? 2 : 1]); }
    for (let y = top + s; y < L.base - 7; y++) { put(L.x, y, IR[0]); if (big) put(L.x + 1, y, IR[2]); }
    for (const ry of [L.base - 20, top + 2 * s + 4]) for (let k = -1; k <= (big ? 2 : 1); k++) put(L.x + k, ry, k < 0 ? IR[0] : IR[1]);
    for (const sx of [-1, 1]) {                                                // two scrolled arms to the side globes
      pline(put, L.x, top + 2 * s, L.x + sx * Math.round(s * 0.6), top + 2 * s - 1, IR[1]);
      pline(put, L.x + sx * Math.round(s * 0.6), top + 2 * s - 1, L.x + sx * s, top + s + 2, IR[1]);
      put(L.x + sx * Math.round(s * 0.4), top + 2 * s + 1, IR[1]);
    }
    for (let y = top + 2; y < top + s + 1; y++) put(L.x, y, IR[1]);
  }
  return p.done();
}
function lampGlobes(L) { const top = lampTop(L), s = lampS(L); return [[L.x, top], [L.x - s, top + s], [L.x + s, top + s]]; }

/* ================= An almond tree at the left edge: broad rounded tiers of big leaves, lit from below by the lamp ================= */
const TREE_MASSES = [[14, 68, 24, 10], [36, 84, 18, 9], [4, 90, 18, 12], [26, 104, 24, 10], [2, 120, 16, 11], [32, 124, 14, 8], [14, 140, 22, 9]];
function renderTree() {
  const p = painter(W, H), { put } = p;
  for (let y = 70; y < 213; y++) {                                             // the trunk, leaning a little, lit on its right
    const x = Math.round(12 + (212 - y) * 0.04), w = y > 200 ? 6 : y > 150 ? 5 : 3;
    for (let k = 0; k < w; k++) put(x + k - (w > 4 ? 1 : 0), y, k === w - 1 ? '#5a4232' : k === 0 ? '#120c0e' : '#2a1e1c');
  }
  for (let k = -3; k < 9; k++) put(11 + k, 213, '#0e0a0c');
  for (const [cx, cy] of TREE_MASSES) { const tx = Math.round(12 + (212 - cy) * 0.04); pline(put, tx + 1, cy + 6, cx, cy + 1, '#22181a'); }
  for (const [cx, cy, rx, ry] of TREE_MASSES) for (let y = Math.floor(cy - ry - 1); y <= cy + ry + 1; y++) for (let x = Math.max(0, Math.floor(cx - rx - 1)); x <= cx + rx + 1; x++) {
    const d = ((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2 + (hash(x >> 1, y >> 1, 616) - 0.5) * 0.55;
    if (d > 1) continue;
    const lamp = Math.max(0, 1 - Math.hypot((x - LAMPS[0].x) / 46, (y - lampTop(LAMPS[0]) - 8) / 34));
    const low = y > cy + ry * 0.15, rim = d > 0.72 && y < cy - ry * 0.3, red = false;
    put(x, y, red ? '#9a3a22' : rim ? '#3e5a46' : lamp > 0.25 && low ? pick(['#a8963e', '#7a7a30', '#4e5e26'], 0.9 - lamp, x, y)
      : pick(['#2e4a2c', '#22381f', '#182a17', '#0f1c10'], 0.25 + (low ? 0.35 : 0) - lamp * 0.3 + hash(x, y, 615) * 0.25, x, y));
  }
  return p.done();
}

/* ================= A horse carriage waiting close to us (a coche: black calash, red wheels, a grey horse in blinkers) ================= */
// Drawn in unit space (u right from its rear, v up from the ground) and scaled, so its shapes stay whole pixels.
const COCHE = { x: 40, g: 254, s: 1.45 };                                     // rear end column, ground row, scale
const cocheAt = (u, v) => [Math.round(COCHE.x + u * COCHE.s), Math.round(COCHE.g - v * COCHE.s)];
function wheel(put, cx, cy, r, spokes) {
  for (let y = Math.floor(cy - r - 1); y <= cy + r + 1; y++) for (let x = Math.floor(cx - r - 1); x <= cx + r + 1; x++) {
    const d = Math.hypot(x - cx, y - cy), a = Math.atan2(y - cy, x - cx);
    if (d <= r + 0.5 && d > r - 1.6) put(x, y, d > r - 0.4 ? '#1a1012' : '#b8302e');
    else if (d < r - 1.6 && d > 1.8 && Math.abs(frac(a / TAU * spokes + 0.5) - 0.5) < 0.45 * spokes / (TAU * d) + 0.01) put(x, y, '#d8483e');
    else if (d <= 1.8) put(x, y, d < 1 ? '#fff0b0' : '#e8c060');
  }
}
function renderCoche(X, G, s, w = W, h = H, tail = true) {
  const p = painter(w, h), { put } = p;
  const each = (u0, u1, v0, v1, fn) => {                                       // every pixel whose unit point is in the box
    for (let y = Math.floor(G - v1 * s); y <= Math.ceil(G - v0 * s); y++) for (let x = Math.floor(X + u0 * s); x <= Math.ceil(X + u1 * s); x++) {
      const u = (x - X) / s, v = (G - y) / s;
      if (u >= u0 && u <= u1 && v >= v0 && v <= v1) { const c = fn(u, v, x, y); if (c) put(x, y, c); }
    }
  };
  const seg = (u0, v0, u1, v1, w, fn) => each(Math.min(u0, u1) - w, Math.max(u0, u1) + w, Math.min(v0, v1) - w, Math.max(v0, v1) + w, (u, v, x, y) => {
    const L = Math.hypot(u1 - u0, v1 - v0), k = clamp(((u - u0) * (u1 - u0) + (v - v0) * (v1 - v0)) / (L * L), 0, 1);
    return Math.hypot(u - u0 - k * (u1 - u0), v - v0 - k * (v1 - v0)) <= w ? fn(u, v, x, y, k) : null;
  });
  for (let x = X + Math.round(2 * s); x <= X + Math.round(76 * s); x++) { put(x, G + 1, '#0e0a0c'); if (x & 1) put(x, G + 2, '#0e0a0c'); }   // shadow
  const HC = ['#f2ece2', '#dcd4c8', '#b8aea2', '#8e847c'];
  // the horse: the far legs darker, body, chest, tail, a thick neck with its mane, the long head slanting down, harness
  for (const [lu, far, hock] of [[53.5, 1, 1], [68.5, 1, 0], [51, 0, 1], [66, 0, 0]]) each(lu - 1.6, lu + 1.6, 0, 14, (u, v) => {
    const c = lu + (hock && v < 7 ? (7 - v) * -0.12 : 0);
    if (Math.abs(u - c) > 0.75) return null;
    return v < 1.3 ? '#2e2426' : far ? HC[3] : v < 6 ? HC[2] : HC[1];
  });
  each(47, 72, 10, 24, (u, v, x, y) => { const a = (u - 59) / 11.5, b = (v - 17) / 5.6; return a * a + b * b <= 1 ? pick(HC, 0.1 + (1 - b) * 0.16 + (a > 0.6 ? 0.22 : 0) + (b < -0.45 ? 0.24 : 0), x, y) : null; });
  each(64, 71, 12, 22, (u, v, x, y) => { const a = (u - 67) / 3.6, b = (v - 17) / 5; return a * a + b * b <= 1 ? pick(HC, 0.2 + (b < -0.3 ? 0.25 : 0), x, y) : null; });
  if (tail) seg(48, 21, 46.6, 10, 1.1, () => '#cdc4b6');
  seg(66.5, 20, 72, 28.5, 2.5, (u, v, x, y, k) => (u - 66.5) * 8.5 - (v - 20) * 5.5 < -9 ? '#e2dacc' : pick(HC, 0.22 + k * 0.08, x, y));
  seg(72.5, 29.5, 78.6, 23.5, 1.9, (u, v, x, y, k) => k > 0.82 ? HC[3] : pick(HC, 0.12 + k * 0.25, x, y));
  each(71.4, 72.6, 30.5, 33.4, () => HC[2]); each(72.9, 74.1, 30.8, 33, () => HC[1]);
  seg(73.5, 28.6, 75.2, 27.2, 0.6, () => '#1a1214');
  seg(66.8, 23.5, 68.5, 16.5, 0.9, () => '#2a1c1a');
  each(56.5, 61.5, 21.6, 23.2, () => '#2a1c1a'); each(58.5, 59.5, 21.6, 23.2, () => '#e8c060'); each(67, 68, 19.4, 20.4, () => '#e8c060');
  seg(74.5, 27, 78, 24, 0.4, () => '#3a2a24');
  seg(36, 15.5, 67, 18, 0.5, () => '#3a2a26');
  // the carriage: the calash's folding hood, a rounded black tub with a red line, the red seat, footwell and dash,
  // the driver's box on its iron, the mudguard, the brass lamp, the wheels
  each(2, 20, 21, 37, (u, v) => {
    const e = ((u - 5) / 14) ** 2 + ((v - 21) / 15.5) ** 2;
    if (e > 1 || u < 2.5) return null;
    return e > 0.86 && u > 10 ? '#5e5258' : (Math.round(Math.atan2(v - 21, u - 5) * 6) & 1 ? '#221a22' : '#16101a');
  });
  each(3, 25, 9.5, 22, (u, v) => {
    if (u < 7 && Math.hypot(u - 7, v - 13.5) > 4 && v < 13.5) return null;
    if (u > 22 && v > 22 - (u - 22) * 2.4) return null;
    return v > 21 ? '#5a4c52' : v < 10.5 ? '#0e0a0c' : (v > 17.4 && v < 18.4) ? '#a8282c' : '#1a1418';
  });
  each(24, 33, 9.5, 14.5, (u, v) => v > 13.6 ? '#4a3e44' : v < 10.4 ? '#0e0a0c' : '#18121a');
  each(31.5, 34, 9.5, 21, (u, v) => u > 33.2 || v > 20 ? '#4a3e44' : '#16101a');
  each(6, 20, 21, 23.4, (u, v) => v > 22.5 ? '#e85a4a' : '#c83a3a');
  each(33, 43, 23.4, 26, (u, v) => v > 25.1 ? '#4a3e44' : '#1e161c'); seg(40, 23.5, 41.5, 14, 0.5, () => '#2a2026'); each(39, 47, 13, 14, () => '#2a2026');
  each(28.5, 41.5, 12.2, 13.4, () => '#120c10');
  each(34.2, 36, 22, 27, (u) => u < 35.1 ? '#c8a050' : '#8a6a3a');
  wheel(put, Math.round(X + 12 * s), Math.round(G - 8 * s), 8 * s, 12); wheel(put, Math.round(X + 36 * s), Math.round(G - 6 * s), 6 * s, 10);
  return p.done();
}
const COCHE_LAMP = cocheAt(35.1, 24.5);

/* ================= A raspao cart: shaved ice and its bottles of syrup under a bulb ================= */
const RASPAO = { x: 211, g: 199, bulb: [234, 175] };
function renderRaspao(p) {
  const { put } = p, X = RASPAO.x, G = RASPAO.g;
  for (let x = X - 1; x <= X + 27; x++) if (x & 1 || (x > X + 2 && x < X + 24)) put(x, G + 1, '#120c0e');
  for (let x = X; x <= X + 25; x++) for (let y = G - 13; y <= G - 4; y++) {
    const edge = y === G - 13 || x === X || x === X + 25;
    put(x, y, edge ? '#f2e2b8' : y === G - 4 ? '#6a1a20' : pick(['#e8443a', '#c8302c', '#9a2226'], (y - G + 13) / 9 + (x > X + 22 ? 0.3 : 0), x, y));
  }
  signText(put, 'RASPAO', X + 2, G - 11, '#fff2d0');
  const SYR = ['#e8283a', '#3ac850', '#f2d23a', '#8a3ac8', '#f28a2a', '#f26ab0'];
  SYR.forEach((c, i) => { const bx = X + 2 + i * 3; put(bx, G - 19, '#e8e0d0'); for (let y = G - 18; y < G - 13; y++) put(bx, y, y === G - 18 ? mixHex(c, '#ffffff', 0.4) : c); put(bx + 1, G - 16, mixHex(c, '#000000', 0.4)); });
  for (let y = G - 19; y < G - 13; y++) for (let x = X + 20; x <= X + 25; x++) put(x, y, y === G - 19 || x === X + 20 ? '#f4fbff' : '#c8e4ee');   // the ice block
  for (let y = RASPAO.bulb[1] - 4; y < G - 13; y++) put(X + 26, y, '#3a2a24');                                        // the bulb's pole
  for (let x = RASPAO.bulb[0]; x <= X + 26; x++) put(x, RASPAO.bulb[1] - 4, '#3a2a24');
  put(RASPAO.bulb[0], RASPAO.bulb[1] - 3, '#2a2026'); put(RASPAO.bulb[0], RASPAO.bulb[1] - 2, '#2a2026');
  wheel(put, X + 5, G - 3, 3, 6); wheel(put, X + 21, G - 3, 3, 6);
  pline(put, X - 6, G - 15, X, G - 11, '#5a3a2a');                                                                     // the handle
}

// a tinto seller's little cart: thermoses of sweet black coffee and a stack of tiny cups
const TINTO = { x: 104, g: 200 };
function renderTinto(p) {
  const { put } = p, X = TINTO.x, G = TINTO.g;
  for (let x = X - 1; x <= X + 14; x++) if (x & 1 || (x > X + 1 && x < X + 12)) put(x, G + 1, '#120c0e');
  for (let x = X; x <= X + 12; x++) for (let y = G - 9; y <= G - 3; y++) put(x, y, y === G - 9 ? '#e8eef2' : x === X + 12 ? '#5a6a7a' : pick(['#c8d4dc', '#9aaab8', '#6a7a8a'], (y - G + 9) / 6, x, y));
  [['#d8303a', '#8a1a22'], ['#c8ccd4', '#7a7e88'], ['#d8303a', '#8a1a22'], ['#3a6ac8', '#22408a']].forEach(([a, b], i) => {
    const tx = X + 1 + i * 3;
    for (let y = G - 16; y < G - 9; y++) { put(tx, y, y === G - 16 ? '#2a2026' : a); put(tx + 1, y, y === G - 16 ? '#2a2026' : b); }
  });
  for (let y = G - 13; y < G - 9; y++) put(X + 13, y, y === G - 13 ? '#ffffff' : '#e8e4dc');
  wheel(put, X + 2, G - 2, 2, 4); wheel(put, X + 10, G - 2, 2, 4);
}

/* ================= The sweet stalls under the arcade (in front of their sellers), the DULCES board ================= */
const JARS = ['#f4ecd8', '#b8865a', '#8a4a2a', '#e86a9a', '#d8a050', '#f2d0a0', '#7a3a5a'];
function renderStalls() {
  const p = painter(W, H), { put } = p;
  for (const [x0, x1] of [[358, 379], [388, 397]]) {
    for (let x = x0; x <= x1; x++) {
      put(x, 190, '#fff4e0'); put(x, 191, '#d8c8aa');
      for (let y = 192; y < 199; y++) put(x, y, y === 197 ? '#c83a3a' : (x - x0) % 6 === 0 ? '#c8b89a' : '#ecdcbe');
      put(x, 199, '#3a2a26');
    }
    for (let x = x0 + 1; x + 2 <= x1; x += 4) {                                 // glass jars of cocadas, alegrías, panelitas
      const c = JARS[Math.floor(hash(x, 7, 501) * JARS.length)];
      put(x, 184, '#e8c060'); put(x + 1, 184, '#e8c060'); put(x + 2, 184, '#b8903a');
      for (let y = 185; y < 190; y++) { put(x, y, '#e4f0f2'); put(x + 1, y, c); put(x + 2, y, mixHex(c, '#2a1a1a', 0.35)); }
    }
  }
  for (let x = 389; x < 395; x += 4) { put(x, 189, '#f4ecd8'); put(x + 1, 189, '#e8dcc0'); put(x + 2, 189, '#c8b898'); }   // a tray of cocadas
  for (let y = 172; y < 179; y++) for (let x = 356; x <= 380; x++) put(x, y, y === 172 || y === 178 || x === 356 || x === 380 ? '#1e3a30' : '#2e5a48');
  signText(put, 'DULCES', 357, 173, '#fff0c8');
  put(362, 171, '#3a2a24'); put(375, 171, '#3a2a24'); put(362, 170, '#3a2a24'); put(375, 170, '#3a2a24');
  return p.done();
}

/* ================= The rooftop sign beyond the wall: a corozo juice ad on a steel frame, two gooseneck lamps ================= */
// Its own ad: JUGO DE COROZO (corozo, the small red palm fruit Cartagena makes its juice from), a tall glass with ice,
// cream paint in a red border, night-graded so it stays dimmer than the tower. render(t, { pitch }) paints the pitch here.
const BB_LAMPS = [BB.x + 12, BB.x + 36];
const BB_FLICK = [[71.2, 72.6], [183.4, 185.1]];                               // the right lamp stutters now and then
function renderBillboard() {
  const x0 = BB.x - 2, y0 = BB.y - 6, p = painter(BB.w + 4, BB_ROOF - y0 + 1), put = (x, y, c) => p.put(x - x0, y - y0, c);
  const ST = '#2a2630', STL = '#4a4452';
  for (let x = BB.x - 1; x <= BB.x + BB.w; x++) { put(x, BB.y - 1, STL); put(x, BB.y + BB.h, ST); put(x, BB.y + BB.h + 1, '#1a1820'); }
  for (let y = BB.y - 1; y <= BB.y + BB.h; y++) { put(BB.x - 1, y, STL); put(BB.x + BB.w, y, ST); }
  for (const lx of [BB.x + 6, BB.x + BB.w - 7]) for (let y = BB.y + BB.h + 2; y < BB_ROOF; y++) { put(lx, y, ST); put(lx + 1, y, '#141218'); }
  pline(put, BB.x + 7, BB.y + BB.h + 2, BB.x + BB.w - 8, BB_ROOF - 1, '#1e1c24');
  for (const lx of BB_LAMPS) { put(lx - 2, BB.y - 2, ST); put(lx - 2, BB.y - 3, ST); put(lx - 1, BB.y - 4, ST); put(lx, BB.y - 4, STL); put(lx + 1, BB.y - 4, ST); }
  return p.done();
}
function renderBillboardAd() {
  const { w, h } = BB, p = painter(w, h), { put } = p;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const lamp = Math.min(...BB_LAMPS.map(lx => Math.hypot((x - lx + BB.x) / 22, y / 16)));
    put(x, y, x === 0 || y === 0 || x === w - 1 || y === h - 1 ? '#b8242c' : x === 1 || y === 1 || x === w - 2 || y === h - 2 ? '#f8eccc' : pick(['#fbeec4', '#f2e0aa', '#e6cf92', '#d4ba7a'], lamp * 0.8, x, y));
  }
  for (let y = 4; y < 18; y++) {                                               // the glass of corozo juice, ice, a straw
    const hw = 2 + Math.round((y - 4) / 13);
    for (let x = 6 - hw; x <= 6 + hw; x++) {
      const rim = x === 6 - hw || x === 6 + hw, juice = y > 6;
      put(x, y, rim ? '#e8f2f4' : !juice ? '#f4fbfc' : (x === 5 && y > 8) ? '#d84a5a' : y > 15 ? '#6a0e1c' : '#9a1a2a');
    }
  }
  put(5, 8, '#f4fbff'); put(7, 9, '#f4fbff'); put(6, 11, '#e8f4f8');
  for (let k = 0; k < 6; k++) put(8 + Math.round(k * 0.3), 6 - k, k & 1 ? '#3aa858' : '#f4f0e6');
  for (const [cx, cy] of [[3, 18], [9, 18]]) { put(cx, cy, '#a8182a'); put(cx + 1, cy, '#7a0e1e'); put(cx, cy - 1, '#d8384a'); }
  signText(put, 'JUGO DE', 17, 3, '#1e3a6a');
  signText(put, 'COROZO', 15, 9, '#c8202e', { tall: true, gap: 2, shade: '#7a1020' });
  p.done();
  return gradeAd(p.c, { level: 0.8, tint: '#141428', mix: 0.12, wear: 0.35, seed: 41 });
}
function bbLampI(t, i) {
  let v = 0.95 + 0.05 * wave(t, 960, i * 0.3);
  if (i === 1 && inEvent(t, BB_FLICK)) { const q = hash(53, Math.floor(t * 20)); v *= q > 0.45 ? 1 : q > 0.2 ? 0.35 : 0.06; }
  return v;
}
function drawBillboard(t, pitch) {
  ctx.drawImage(BB_C, BB.x - 2, BB.y - 6);
  if (!PITCH.covers(t, pitch)) {
    ctx.drawImage(BB_AD, BB.x, BB.y);
    const li = bbLampI(t, 1);
    if (li < 0.9) { ctx.globalAlpha = (0.9 - li) * 0.5; rect(ctx, BB.x + 24, BB.y, BB.w - 24, BB.h, '#140c10'); ctx.globalAlpha = 1; }
  }
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  BB_LAMPS.forEach((lx, i) => { const li = bbLampI(t, i); rect(ctx, lx - 1, BB.y - 3, 3, 1, li > 0.6 ? '#ffe6b0' : '#6a5040'); halo(HL.bbLamp, lx, BB.y - 1, li * 0.8); });
}

/* ================= People (one person(): a head, a body, legs and a colour each, lit by the plaza's amber light) ================= */
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };
const SKINS = ['#f0c8a0', '#d8a47c', '#b07a52', '#8a5636', '#64402a'], HAIRS = ['#16100e', '#3a2418', '#6a4222', '#c8a060', '#d4d0cc'];
const NIGHT_P = '#141022', GLOWC = '#ffcf90';
/** a look: proportions for a height of h px, colours dimmed for the night (dk) */
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.2, m = c => mixHex(c, NIGHT_P, dk);
  const hh = h >= 22 ? 5 : h >= 14 ? 4 : 3, th = Math.round(h * 0.36);
  return { h, hh, hw: h >= 19 ? 4 : 3, th, tw: h >= 22 ? 7 : h >= 19 ? 6 : 5, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(HAIRS[hair], GLOWC, 0.3), top: m(top), topD: mixHex(top, NIGHT_P, dk + 0.3),
    topL: mixHex(top, GLOWC, 0.35), bot: m(bot), shoe: m('#221a20'), long: o.long || 0, skirt: o.skirt ? m(o.skirt) : null,
    hat: o.hat ? m(o.hat) : null, wrap: o.wrap ? m(o.wrap) : null };
}
/**
 * One person, feet (or seat) on row fy, centred on column x; returns the head's top row.
 * o.view: 0 from behind, 1 facing us, 2 in profile (o.dir 1 right, -1 left); o.step: stride frame; o.sit: seated at fy
 * (o.dangle: legs hang down in front); o.lean: head px towards a neighbour; o.aL / o.aR: 0 down, 1 up, 2 phone, 3 out,
 * 4 bent up, 5 hand to the face
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = o.sit ? 0 : P.ll;
  const ty = fy - legs - th + 1, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1), legC = P.skirt ? P.skin : P.bot;
  if (legs) {
    const ly = fy - legs + 1;
    if (side) {
      if (o.step) {
        R(x - 2, ly, 4, 2, legC);
        R(dir > 0 ? x + 1 : x - 2, ly + 2, 2, legs - 2, legC); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
        R(dir > 0 ? x - 2 : x + 1, ly + 2, 2, legs - 3, legC); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
      } else { R(x - 1, ly, 2, legs, legC); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
      if (P.skirt) R(x - 2, ly, 4, (legs >> 1) + 2, P.skirt);
    } else {
      const lw = bw >= 7 ? 3 : 2;
      R(tx, ly, bw, 2, P.bot); R(tx, ly + 2, lw, legs - 2, legC); R(tx + bw - lw, ly + 2, lw, legs - 2, legC);
      R(tx, fy, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
      if (P.skirt) R(tx - 1, ly, bw + 2, (legs >> 1) + 2, P.skirt);
    }
  } else if (o.sit && o.dangle) {
    const n = o.dangle;
    R(tx, fy - 1, bw, 2, P.skirt || P.bot); R(tx, fy + 1, 2, n, legC); R(tx + bw - 2, fy + 1, 2, n, legC);
    R(tx, fy + n + 1, 2, 1, P.shoe); R(tx + bw - 2, fy + n + 1, 2, 1, P.shoe);
  } else if (o.sit && side) {
    R(dir > 0 ? x - 1 : x - 4, fy - 1, 5, 2, P.bot); R(dir > 0 ? x + 3 : x - 4, fy + 1, 2, o.shin || 0, legC);
    if (o.shin) R(dir > 0 ? x + 3 : x - 5, fy + 1 + o.shin, 3, 1, P.shoe);
  } else if (o.sit) R(tx - 1, fy - 1, bw + 2, 2, P.bot);
  R(tx, ty, bw, th, P.top);
  if (!side) R(tx + bw - 1, ty + 1, 1, th - 1, P.topD);
  if (view !== 1) R(tx, ty, bw, 1, P.topL);
  if (side) {
    const st = o.aR || 0;
    if (st === 0) { const ax = x + (o.step ? -dir : 0); R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.topD); R(x + dir * 4, ty + 2, 1, 1, P.skin); }
    else { R(x + dir, ty + 1, 1, 2, P.topD); R(x + dir * 2, ty, 1, 1, P.skin); }
  } else {
    const arm = (ax, st, sg) => {
      if (!st) { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
      else if (st === 1) { R(ax, hy - 1, 1, ty - hy + 2, P.topD); R(ax, hy - 3, 1, 2, P.skin); }
      else if (st === 2 || st === 4) {
        R(ax, hy + 2, 1, ty - hy, P.topD); R(ax, hy + 1, 1, 1, P.skin);
        if (st === 2) { const p0 = sg < 0 ? ax - 1 : ax; R(p0, hy - 2, 2, 3, '#0e1018'); R(p0, hy - 2, 2, view === 0 ? 1 : 0, '#ffe2a8'); }
      } else if (st === 3) { R(sg < 0 ? ax - 2 : ax, ty + 1, 3, 1, P.topD); R(sg < 0 ? ax - 3 : ax + 3, ty + 1, 1, 1, P.skin); }
      else { R(ax, ty, 1, 2, P.topD); R(ax + (sg < 0 ? 1 : -1), hy + hh - 2, 1, 2, P.skin); }
    };
    arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1);
  }
  const hx = x - (hw >> 1) + (o.lean || 0), hr = hh >= 5 ? 2 : 1;
  if (view === 0) {
    R(hx, hy, hw, hh, P.hair); R(hx, hy, hw, 1, P.hairL);
    if (P.long) R(hx, hy + hh, hw, P.long, P.hair); else R(hx + 1, hy + hh - 1, hw - 2, 1, P.skin);
  } else if (view === 1) {
    R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair);
    if (P.long) { R(hx - 1, hy + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hy + 1, 1, hh + P.long - 1, P.hair); }
  } else { R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair); R(dir > 0 ? hx : hx + hw - 1, hy, 1, hh - 1 + P.long, P.hair); }
  if (P.wrap) { R(hx - 1, hy - 1, hw + 2, 3, P.wrap); R(hx, hy - 2, hw, 1, P.wrap); }
  if (P.hat) { R(hx, hy - 1, hw, 2, P.hat); if (side) R(dir > 0 ? hx + hw : hx - 1, hy, 1, 1, P.hat); else R(hx - 1, hy, hw + 2, 1, P.hat); }
  return hy;
}
const TOPS = ['#f4f0e6', '#d8404a', '#f2c84a', '#3a6ac8', '#2a9a8a', '#e87aa8', '#e8823a', '#4a9a4a', '#8a5ac8', '#58b0d8'];
const BOTS = ['#33466e', '#22202c', '#b09a70', '#d8d4cc', '#3a3a58', '#4a3a30'];
const WALL_WALK = [                                                            // [crossings a loop, phase, direction, rank, look]
  { k: 2, ph: 0.1, dir: 1, rank: 0.15, P: look(11, 2, 0, '#f4f0e6', BOTS[1]), mate: look(10, 1, 1, '#e87aa8', BOTS[3], { long: 1 }) },
  { k: 1, ph: 0.55, dir: -1, rank: 0.35, P: look(11, 3, 0, '#3a6ac8', BOTS[2]) },
  { k: 3, ph: 0.8, dir: 1, rank: 0.7, P: look(11, 0, 3, '#f2c84a', BOTS[0], { long: 2 }) },
];
const WALL_SIT = [{ x: 64, rank: 0.05, P: look(10, 2, 0, '#d8404a', BOTS[0]) }, { x: 70, rank: 0.05, P: look(10, 1, 1, '#f4f0e6', BOTS[1], { long: 2 }) }];
function drawWallWalk(t, crowd) {
  for (const w of WALL_WALK) {
    if (w.rank >= crowd) continue;
    const u = frac(t * w.k / LOOP + w.ph), x = w.dir > 0 ? -12 + u * (PORTAL.x0 + 24) : PORTAL.x0 + 12 - u * (PORTAL.x0 + 24);
    const s = (step(t, 384 * w.k) + (w.dir > 0 ? 0 : 1)) & 1, fy = wallTop(Math.round(clamp(x, 0, PORTAL.x0 - 1))) + 2;
    person(x, fy, w.P, { view: 2, dir: w.dir, step: s });
    if (w.mate) person(x - 4 * w.dir, fy, w.mate, { view: 2, dir: w.dir, step: 1 - s });
  }
  for (const q of WALL_SIT) if (q.rank < crowd) person(q.x, wallTop(q.x) - 5, q.P, { sit: 1, view: 1, lean: q.x === 70 ? -1 : 0 });
}

/* ----- who is there (the concept's spots and scale; phase 2 gives each a verb on the timeline) ----- */
// rank: the share of the crowd at which they are drawn (rank 0: the vendors and the coachman, there at every level)
const CAST = [
  { x: 122, y: 200, rank: 0, P: look(22, 2, 4, '#e8e4d8', BOTS[5], { hat: '#d8c8a0' }), o: { view: 2, dir: -1, aR: 3 } },
  { x: 166, y: 201, rank: 0.1, P: look(23, 2, 0, TOPS[3], BOTS[2]), o: {} },
  { x: 173, y: 201, rank: 0.1, P: look(22, 1, 1, TOPS[5], BOTS[1], { long: 3, skirt: '#e8e0d0' }), o: { lean: -1 } },
  { x: 88, y: 199, rank: 0.65, P: look(22, 4, 0, TOPS[6], BOTS[0]), o: { view: 1, aL: 4 } },
  { x: 146, y: 202, rank: 0.4, P: look(23, 1, 0, TOPS[1], BOTS[4]), o: { view: 2, dir: 1, step: 1 } },
  { x: 238, y: 203, rank: 0.3, P: look(15, 3, 1, TOPS[2], BOTS[1], { long: 1 }), o: { view: 2, dir: -1, aR: 3 } },
  { x: 246, y: 203, rank: 0.3, P: look(23, 3, 0, TOPS[4], BOTS[3], { long: 2 }), o: { view: 2, dir: -1 } },
  { x: 269, y: 205, rank: 0.35, P: look(23, 0, 3, TOPS[9], BOTS[0]), o: { aR: 2 } },
  { x: 280, y: 194, rank: 0.45, P: look(21, 3, 0, TOPS[0], BOTS[1]), o: { sit: 1, view: 1, dangle: 2 } },
  { x: 305, y: 194, rank: 0.75, P: look(21, 0, 4, TOPS[7], BOTS[2], { long: 2 }), o: { sit: 1, view: 1, dangle: 2, aR: 2 } },
  { x: 326, y: 204, rank: 0, P: look(23, 4, 0, '#f2c84a', BOTS[1], { skirt: '#e8443a', wrap: '#f4f0e6' }), o: { view: 2, dir: -1, aR: 3 }, prop: 'bowl' },
  { x: 30, y: 206, rank: 0.55, P: look(24, 2, 0, TOPS[6], BOTS[0]), o: { view: 2, dir: 1 } },
  { x: 337, y: 200, rank: 0.9, P: look(22, 1, 2, TOPS[8], BOTS[3], { long: 3 }), o: { view: 1 } },
];
const DRIVER = { x: cocheAt(38, 0)[0], y: cocheAt(0, 25)[1], P: look(27, 3, 0, '#f4f0e6', BOTS[1], { hat: '#2a2a34', dk: 0.12 }) };   // on the near coche's box
const ACTS = ['tinto', 'couple', 'kiss', 'idle', 'look', 'girl', 'mother', 'film', 'chat', 'phone', 'offer', 'idle', 'chat'];
/** what each of CAST is doing at t: periods that divide the loop, offset by their column; the fireworks turn heads */
function poseOf(c, act, t) {
  const o = { ...c.o }, ph = c.x * 0.013, fw = fwOn(t);
  switch (act) {
    case 'tinto': o.aR = frac(t / 16) > 0.2 && frac(t / 16) < 0.4 ? 3 : 0; break;
    case 'couple': if (fw) o.aL = 1; break;
    case 'kiss': o.lean = frac(t / 40 + 0.3) < 0.06 ? -2 : -1; if (fw) o.aR = 2; break;
    case 'look': o.view = 0; o.step = 0; if (fw) o.aR = 2; break;
    case 'girl': o.aR = fw ? 1 : frac(t / 12) > 0.75 ? 3 : 0; break;
    case 'mother': if (fw) o.aL = 1; break;
    case 'film': o.aR = frac(t / 24 + ph) < 0.7 || fw ? 2 : 0; break;
    case 'chat': o.lean = frac(t / 8 + ph) < 0.3 ? 1 : 0; if (fw) o.view = 0; break;
    case 'phone': if (fw) o.view = 0; break;
    case 'offer': o.aR = frac(t / 10 + ph) < 0.5 ? 3 : 0; break;
    default: break;
  }
  return o;
}
const VENDOR = { x: 221, y: 196, P: look(23, 4, 0, '#f4f0e6', BOTS[0], { hat: '#e8dcc0' }) };                 // behind the raspao cart
const SELLER = { x: 374, y: 192, P: look(22, 4, 0, '#f4f0e6', '#3a6ac8', { wrap: '#e85a7a', dk: 0.1 }) };   // behind her jars
const BUYER = { x: 363, y: 204, rank: 0.2, P: look(23, 1, 2, TOPS[9], BOTS[0], { dk: 0.12 }) };
const NEIGHBOUR = { x: 393, y: 159, rank: 0.4, P: look(20, 2, 1, TOPS[8], BOTS[3], { long: 3, dk: 0.1 }) };     // in the balcony's open door
const ARC_BULBS = [[369, 182], [393, 178]];
const NSELLER = { x: 454, y: 186, P: look(30, 4, 0, '#f4f0e6', '#e85a7a', { wrap: '#f2c84a', dk: 0.08 }) };          // the near stall's seller
const NBUYER = { x: 424, y: NEAR.floor, rank: 0.1, P: look(31, 1, 2, TOPS[3], BOTS[3], { long: 4, dk: 0.06 }) };   // a customer choosing
const NKID = { x: 467, y: NEAR.floor, rank: 0.1, P: look(18, 3, 1, TOPS[2], BOTS[1], { dk: 0.06 }) };              // her daughter, reaching up
const CROWD = 0.5;
function renderRail() {                                                       // the balcony's balustrade, drawn again over the neighbour
  const p = painter(W, H), { put } = p;
  for (let x = 386; x < NEAR.x0; x++) {
    put(x, CORAL.rail, WOOD[0]); put(x, CORAL.rail + 1, WOOD[2]);
    for (let y = CORAL.rail + 2; y < CORAL.deck; y++) if ((x & 1) === 0) put(x, y, (y - CORAL.rail) % 4 === 1 ? WOOD[0] : WOOD[2]);
  }
  return p.done();
}
function drawBowl(x, y) {                                                     // the palenquera's basin of fruit on a low stool
  R(x - 4, y - 5, 1, 6, '#4a3020'); R(x + 3, y - 5, 1, 6, '#4a3020'); R(x - 5, y - 6, 10, 1, '#7a5034');
  R(x - 6, y - 9, 12, 3, '#c8c0b0'); R(x - 5, y - 7, 10, 1, '#8a8478');
  const FR = [['#ffb040', '#c8701e'], ['#ffe24a', '#c8a02a'], ['#e8483a', '#a82a2a'], ['#7ac84a', '#4a8a2a'], ['#ff9a6a', '#c86040']];
  FR.forEach(([a, b], i) => { R(x - 5 + i * 2, y - 11 - (i & 1), 2, 2, a); R(x - 4 + i * 2, y - 10 - (i & 1), 1, 1, b); });
}

/* ================= Bougainvillea petals, the city's own weather: magenta bracts on the breeze at three depths ================= */
const PCOL = [['#d6247e', '#8a1060'], ['#b5179e', '#6a0e5a'], ['#ff6ab8', '#c8287e']];
const PETALS = [0, 1, 2].map(d => Array.from({ length: [34, 22, 9][d] }, (_, i) => ({
  k: [4, 7, 11][d] + Math.floor(hash(i, d, 701) * 3), ph: hash(i, d, 702), h: hash(i, d, 703), c: PCOL[Math.floor(hash(i, d, 704) * 3)],
})));
function drawPetals(t, d) {
  for (const q of PETALS[d]) {
    const u = frac(t * q.k / LOOP + q.ph), fade = Math.min(1, Math.sin(Math.PI * u) * 3);
    let x, y;
    if (d === 0) { x = 380 - u * 270 + Math.sin(TAU * (u * 3 + q.ph)) * 4; y = 130 + q.h * 40 + u * 26 + Math.sin(TAU * (u * 5 + q.h)) * 2; }
    else if (d === 1) { x = 402 + q.h * 42 - u * (170 + q.h * 70) + Math.sin(TAU * (u * 2 + q.ph)) * 5; y = lerp(86 + q.h * 84, 196 + q.h * 10, u) + Math.sin(TAU * (u * 4 + q.h)) * 2; }
    else { x = 500 - u * 540; y = lerp(70 + q.h * 90, 230 + q.h * 30, u) + Math.sin(TAU * (u * 3 + q.ph)) * 8; }
    x = Math.round(x); y = Math.round(y);
    const fl = (step(t, d === 2 ? 1440 : 960) + Math.floor(q.ph * 7)) % 3;
    ctx.globalAlpha = fade * (d === 0 ? 0.7 : 0.95);
    if (d === 0) R(x, y, 1, 1, q.c[0]);
    else if (d === 1) { if (fl === 0) R(x, y, 2, 1, q.c[0]); else if (fl === 1) R(x, y, 1, 2, q.c[0]); else { R(x, y, 1, 1, q.c[0]); R(x + 1, y, 1, 1, q.c[1]); } }
    else if (fl === 0) { R(x, y, 3, 2, q.c[0]); R(x + 1, y + 2, 1, 1, q.c[1]); R(x + 2, y, 1, 1, q.c[1]); }
    else if (fl === 1) { R(x + 1, y, 1, 3, q.c[0]); R(x, y + 1, 1, 1, q.c[1]); }
    else { R(x, y, 2, 2, q.c[1]); R(x + 2, y + 1, 1, 1, q.c[0]); }
  }
  ctx.globalAlpha = 1;
}

/* ================= A picked weather (site/js/engine/weather.js) ================= */
// The city's own falling things are the petals; rain, snow and leaves picks fall at three depths: far onto the plaza by
// the wall, mid onto the plaza by the statue and the carts, near past the tree and the arcade.
const WEATHER = weatherPlans(['petals']);
const WX_LAND = [[176, 190], [190, 206], [207, H + 4]];
const WX_LIGHTS = [
  ...LAMPS.flatMap(L => lampGlobes(L).map(([x, y]) => ({ x, y: y + 4, rx: 20 + (L.h >> 3), ry: 26 + (L.h >> 3), col: '#ffc890' }))),
  ...LANTERNS.map(x => ({ x, y: LANTERN_Y + 2, rx: 16, ry: 18, col: '#ffd8a0' })),
  ...ARC_BULBS.map(([x, y]) => ({ x, y, rx: 14, ry: 16, col: '#ffd8a0' })), { x: N_BULB[0], y: N_BULB[1], rx: 30, ry: 34, col: '#ffd8a0' },
  { x: RASPAO.bulb[0], y: RASPAO.bulb[1], rx: 12, ry: 14, col: '#ffe0b0' },
];
const WX = { land: WX_LAND, lights: WX_LIGHTS };
const WX_BB = { land: WX_LAND, lights: [...BB_LAMPS.map(x => ({ x, y: BB.y + 4, rx: 18, ry: 12, col: '#ffe6b0' })), ...WX_LIGHTS] };

function renderVignette() {
  const p = painter(W, H);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const d = Math.hypot((x - W / 2) / (W * 0.62), (y - H * 0.45) / (H * 0.7));
    if (d > 0.82 && (d - 0.82) * 2.4 > bayer(x, y)) p.put(x, y, '#05040a', 70);
  }
  return p.done();
}

/* ================= The loop's timeline (seconds) =================
   always       stars, wisps, the lanterns' and lamps' light breathing, the near bulb swaying on its wire, reflections,
                petals at three depths, moths at every light, bats round the belfry, strollers on the wall walk and the
                plaza, the near horse's tail, the coachman dozing; vendors at work: raspao served every 12 s, a tinto
                poured every 16 s, the sellers fanning, the mother choosing, her daughter hopping
     24-70      a coche comes out of the central passage (its harness bells at 26) and crosses the plaza to the left
     71, 183    the rooftop sign's right lamp stutters
    100-109     the clock strikes four (bells at 100, 103, 106, 109): the dial glows, rings widen, the bats start up
    154-196     fireworks over the gate for the Fiestas de Independencia (8 bursts, 156-189): people point and film
    200-228     the neighbour on the near balcony waters the bougainvillea: drops onto the plaza, a flurry of petals
    34-56, 126-148  a paper lantern drifts up over the far roofs and hovers (one of the hidden things)
   Every period divides 240; every event sits well inside (0, 240).                                                    */
const COCHE2 = { t0: 24, t1: 70, g: 189 };
const BELLS = [100, 103, 106, 109];
const FIREWORKS = [[156, 282, 58, 0], [160.5, 128, 64, 1], [164, 292, 40, 2], [168.5, 254, 76, 3], [173, 140, 44, 0],
  [178, 316, 64, 1], [183.5, 262, 38, 2], [189, 118, 56, 3]];                 // [burst time, x, y, colours]
const FW_COLS = [['#ffe08a', '#ffa040'], ['#ff7ac0', '#b5179e'], ['#a8f080', '#3ac850'], ['#fff6e0', '#9ab8ff']];
const FW_REACT = [154, 196];
const WATER = { t0: 200, t1: 228 };
const LANTERN_UP = [[34, 56], [126, 148]];
const PAPER = { x: 360, y: 50 };                                               // the paper lantern's box (13 x 24)
const IGUANA = { x: 337, y: 132 };                                             // on the wall's coping
const CAT = { x: 361, y: 200 };                                                // asleep under the DULCES stall
const PIGEONS = [118, 122, 127, 131];                                          // on the wall walk's parapet, left of the tower
const fwOn = t => t >= FW_REACT[0] && t < FW_REACT[1];

/* ----- the second coche, out of the central passage and across to the left ----- */
const COACH2 = look(19, 2, 0, '#f4f0e6', BOTS[1], { hat: '#2a2a34' });
function drawCoche2(t, crowd) {
  if (t < COCHE2.t0 || t >= COCHE2.t1) return;
  const u = (t - COCHE2.t0) / (COCHE2.t1 - COCHE2.t0), x = Math.round(lerp(180, -76, u)), y = COCHE2.g - 34;
  const bob = (step(t, 1152) & 1) && u > 0.04 ? 1 : 0;
  ctx.globalAlpha = smooth(0, 0.06, u);                                        // out of the passage's dark
  ctx.drawImage(COCHE2_C, x, y);
  if (crowd > 0) person(x + 37, y + 13 - bob, COACH2, { sit: 1, view: 2, dir: -1, aR: 3, shin: 5 });
  ctx.globalAlpha = 1;
  rect(ctx, x + 39, y + 12, 1, 2, '#fff2c8'); halo(HL.coche, x + 39, y + 13, smooth(0, 0.06, u) * 0.9);
}
/* ----- strollers crossing the plaza behind the carts, a whole number of times a loop ----- */
const PWALK = [
  { k: 2, ph: 0.18, dir: -1, rank: 0.25, P: look(20, 3, 0, '#e8e4d8', BOTS[0], { hat: '#d8c8a0' }) },
  { k: 3, ph: 0.62, dir: 1, rank: 0.5, P: look(20, 1, 1, TOPS[8], BOTS[3], { long: 3 }), mate: look(20, 2, 0, TOPS[9], BOTS[0]) },
  { k: 1, ph: 0.42, dir: 1, rank: 0.08, P: look(19, 4, 4, '#c8c0b0', BOTS[5]), cane: true },
];
function drawPlazaWalkers(t, crowd) {
  for (const w of PWALK) {
    if (w.rank >= crowd) continue;
    const u = frac(t * w.k / LOOP + w.ph), x = w.dir > 0 ? -14 + u * (PORTAL.x0 + 20) : PORTAL.x0 + 6 - u * (PORTAL.x0 + 20);
    const s = (step(t, 768) + (w.dir > 0 ? 0 : 1)) & 1;
    person(x, 193, w.P, { view: 2, dir: w.dir, step: s });
    if (w.mate) person(x - 7 * w.dir, 193, w.mate, { view: 2, dir: w.dir, step: 1 - s });
    if (w.cane) R(Math.round(x) + w.dir * 3, 185, 1, 9, '#6a4a2a');
  }
}
/* ----- the strollers' and the passing coche's shadows, thrown up onto the lit wall by the plaza's lamps ----- */
const SH = '#140a0a';
const SHADOW_P = h => ({ h, hh: 6, hw: 5, th: Math.round(h * 0.36), tw: 9, ll: h - 6 - Math.round(h * 0.36), skin: SH, hair: SH, hairL: SH, top: SH, topD: SH, topL: SH, bot: SH, shoe: SH, long: 0, skirt: null, hat: null, wrap: null });
const SHADOW_W = SHADOW_P(30);
function lampFor(x) { return Math.abs(x - LAMPS[0].x) < Math.abs(x - LAMPS[1].x) ? LAMPS[0] : LAMPS[1]; }
function drawWallShadows(t, crowd) {
  ctx.save(); ctx.beginPath(); ctx.rect(0, 0, PORTAL.x0, WALL_FOOT + 3); ctx.clip();
  const cast = (x, draw) => {
    const L = lampFor(x), a = 0.3 * Math.max(0, 1 - Math.abs(x - L.x) / 140);
    if (a < 0.02) return;
    ctx.globalAlpha = a; draw(Math.round(x + (x - L.x) * 0.35));
  };
  for (const w of PWALK) {
    if (w.rank >= crowd) continue;
    const u = frac(t * w.k / LOOP + w.ph), x = w.dir > 0 ? -14 + u * (PORTAL.x0 + 20) : PORTAL.x0 + 6 - u * (PORTAL.x0 + 20);
    const s = (step(t, 768) + (w.dir > 0 ? 0 : 1)) & 1;
    cast(x, sx => person(sx, wallFoot(clamp(sx, 0, PORTAL.x0 - 1)) - 1, SHADOW_W, { view: 2, dir: w.dir, step: s }));
    if (w.mate) cast(x - 7 * w.dir, sx => person(sx, wallFoot(clamp(sx, 0, PORTAL.x0 - 1)) - 1, SHADOW_W, { view: 2, dir: w.dir, step: 1 - s }));
  }
  if (t >= COCHE2.t0 && t < COCHE2.t1) {
    const u = (t - COCHE2.t0) / (COCHE2.t1 - COCHE2.t0), x = lerp(180, -76, u);
    cast(x + 35, sx => ctx.drawImage(COCHE2_SH, sx - 49, WALL_FOOT - 50, 98, 50));
  }
  ctx.globalAlpha = 1; ctx.restore();
}
/* ----- the clock striking: the dial glows, two rings widen from the belfry ----- */
function drawBells(t) {
  for (const b of BELLS) {
    const tau = t - b;
    if (tau < 0 || tau > 2.6) continue;
    halo(HL.dialStrike, TX, TR.dial, (1 - tau / 2.6) * 0.9);
    ctx.lineWidth = 1;
    for (const k of [0, 0.7]) {
      const q = tau - k;
      if (q < 0 || q > 1.9) continue;
      ctx.strokeStyle = `rgba(255,236,190,${(0.3 * (1 - q / 1.9)).toFixed(3)})`;
      ctx.beginPath(); ctx.arc(TX + 0.5, TR.dial - 26.5, 14 + q * 20, 0, TAU); ctx.stroke();
    }
  }
}
/* ----- bats round the floodlit belfry (startled by the bells) ----- */
const BATS = Array.from({ length: 3 }, (_, i) => ({ k: 8 + i * 3, k2: 13 + i * 4, ph: i * 0.31, rx: 30 + i * 5, ry: 10 + i * 4, cy: 34 + i * 8 }));
function drawBats(t) {
  const startle = BELLS.reduce((a, b) => Math.max(a, env(t, b, b + 9, 1.5)), 0);
  for (const b of BATS) {
    const a = TAU * (b.k * t / LOOP + b.ph), rr = 1 + startle * 0.6;
    const x = Math.round(TX + Math.sin(a) * b.rx * rr), y = Math.round(b.cy + Math.sin(TAU * (b.k2 * t / LOOP + b.ph)) * b.ry * rr - startle * 10);
    const fl = (step(t, 2880) + b.k) & 1;
    R(x, y, 1, 1, '#1a1622'); R(x - 1, y - fl, 1, 1, '#2a2432'); R(x + 1, y - fl, 1, 1, '#2a2432');
    if (!fl) { R(x - 2, y + 1, 1, 1, '#2a2432'); R(x + 2, y + 1, 1, 1, '#2a2432'); }
  }
}
/* ----- November fireworks low over the gate (beyond the walls, over the bay) ----- */
const FW_P = Array.from({ length: 34 }, (_, i) => ({ a: (i + hash(i, 1, 801) * 0.4) / 34 * TAU, v: 0.65 + hash(i, 2, 801) * 0.35, id: i }));
function drawFireworks(t, reducedMotion) {
  for (const [t0, fx, fy, ci] of FIREWORKS) {
    const tau = t - t0;
    if (tau < -1.4 || tau > 4.2) continue;
    const [c1, c2] = FW_COLS[ci];
    if (tau < 0) {                                                             // the rocket climbing from behind the wall
      const k = (tau + 1.4) / 1.4, ry = Math.round(lerp(wallTop(fx) - 6, fy, k * (2 - k)));
      px(ctx, fx, ry, '#fff0c0'); ctx.globalAlpha = 0.55; px(ctx, fx, ry + 1, '#ffb060'); px(ctx, fx, ry + 2, '#a86030'); ctx.globalAlpha = 1;
      continue;
    }
    const R0 = 22 * (1 - Math.exp(-tau * 1.7)), fall = tau * tau * 1.3, fade = 1 - smooth(1.4, 4.2, tau);
    halo(HL.fw[ci], fx, fy + fall * 0.5, fade * (reducedMotion ? 0.3 : 0.9) * (1 - smooth(0, 1.4, tau) * 0.4));
    for (const q of FW_P) {
      const x = Math.round(fx + Math.cos(q.a) * R0 * q.v), y = Math.round(fy + Math.sin(q.a) * R0 * q.v * 0.9 + fall);
      ctx.globalAlpha = fade * (tau > 1.6 && hash(q.id, step(t, 2400), ci) < 0.3 ? 0.25 : 1);
      px(ctx, x, y, tau < 0.9 ? c1 : c2);
      if (tau < 2.4) { const x2 = Math.round(fx + Math.cos(q.a) * R0 * q.v * 0.9), y2 = Math.round(fy + Math.sin(q.a) * R0 * q.v * 0.81 + fall * 0.95); if (x2 !== x || y2 !== y) px(ctx, x2, y2, c1); }
      if (tau < 1.8) { ctx.globalAlpha = fade * 0.45; px(ctx, Math.round(fx + Math.cos(q.a) * R0 * q.v * 0.75), Math.round(fy + Math.sin(q.a) * R0 * q.v * 0.68 + fall * 0.8), c2); }
    }
    ctx.globalAlpha = 1;
  }
}
// each burst lights the gate wall and the tower for a moment, in its colour (left out with reduced motion)
function fireworkLight(t) {
  for (const [t0, fx, , ci] of FIREWORKS) {
    const tau = t - t0;
    if (tau < 0 || tau > 2.2) continue;
    halo(HL.fwWall[ci], clamp(fx, 120, 300), 150, 1 - tau / 2.2);
  }
}
/* ----- steam off the warm wet bricks after the shower: low mist drifting slowly across the plaza ----- */
function renderMist(w, h, seed) {
  const r = rng(seed), p = painter(w, h), puffs = [];
  for (let i = 0; i < 10; i++) puffs.push({ x: 10 + r() * (w - 20), y: h * (0.4 + r() * 0.3), rx: 12 + r() * 22, ry: 2.5 + r() * 2.5 });
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) {
    let v = 0;
    for (const q of puffs) v = Math.max(v, 1 - ((i - q.x) / q.rx) ** 2 - ((y - q.y) / q.ry) ** 2);
    v *= Math.min(1, i / 20, (w - 1 - i) / 20);
    if (v > bayer(i, y) * 1.1) p.put(i, y, v > 0.6 ? '#c8a888' : '#a88c78');
  }
  return p.done();
}
const MISTS = [{ y: 178, w: 170, h: 12, k: 2, ph: 0.1, a: 0.22 }, { y: 190, w: 200, h: 14, k: 3, ph: 0.55, a: 0.2 }, { y: 206, w: 230, h: 16, k: 2, ph: 0.8, a: 0.17 },
  { y: 224, w: 240, h: 18, k: 1, ph: 0.35, a: 0.14 }, { y: 244, w: 220, h: 18, k: 2, ph: 0.05, a: 0.12 }];
function drawMist(t) {
  MISTS.forEach((m, i) => {
    const span = W + m.w, x = Math.round(frac(t * m.k / LOOP + m.ph) * span) - m.w;
    ctx.globalAlpha = m.a * (0.75 + 0.25 * wave(t, 6 + i, i * 0.3));
    ctx.drawImage(MIST_C[i], x, m.y);
  });
  ctx.globalAlpha = 1;
}
/* ----- cars on the avenue outside the gate: their lights sweep through the passages and spill onto the wet plaza ----- */
const SWEEPS = Array.from({ length: 14 }, (_, i) => ({ t: 6 + i * 17 + hash(i, 1, 811) * 6, g: GATE[Math.floor(hash(i, 2, 811) * 3)] }));
function drawSweeps(t) {
  for (const sw of SWEEPS) {
    const tau = t - sw.t;
    if (tau < 0 || tau > 3) continue;
    const a = Math.sin(Math.PI * tau / 3) * 0.75, G = sw.g, cx = G.x + G.w / 2, foot = wallFoot(Math.round(cx));
    halo(HL.arch, cx, foot - 8, a);
    ctx.globalCompositeOperation = 'lighter';
    for (let y = foot; y < foot + 24; y++) {
      const k = (y - foot) / 24, hw = G.w * 0.4 + k * 18;
      ctx.globalAlpha = a * 0.16 * (1 - k);
      ctx.fillStyle = '#ffd8a0'; ctx.fillRect(Math.round(cx - hw + (tau - 1.5) * 4 * k), y, Math.round(hw * 2), 1);
    }
    ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  }
}
/* ----- drips from the near balcony into the puddle under it, rings spreading ----- */
function drawDrips(t) {
  const { cyc, u } = cycle(t, 40, 0.2);
  const x = 432 + Math.floor(hash(cyc, 3, 821) * 18), q = PUDDLES[4];
  if (u < 0.12) { ctx.globalAlpha = 0.8; R(x, Math.round(lerp(NEAR.deck + 4, q.cy, u / 0.12)), 1, 2, '#cfe6f2'); ctx.globalAlpha = 1; return; }
  const r = (u - 0.12) * 40;
  if (r > 16) return;
  ctx.globalAlpha = 0.5 * (1 - r / 16);
  R(Math.round(x - r), q.cy, Math.max(1, Math.round(r * 0.6)), 1, '#e8d8c0'); R(Math.round(x + r * 0.4), q.cy, Math.max(1, Math.round(r * 0.6)), 1, '#e8d8c0');
  ctx.globalAlpha = 1;
}
/* ----- the neighbour waters the bougainvillea on the near balcony: drops onto the plaza, petals shaken loose ----- */
const WATERER = look(28, 3, 1, '#7ac0e0', BOTS[3], { long: 4, dk: 0.06 });
function drawWatering(t, crowd) {
  if (t < WATER.t0 || t >= WATER.t1 || crowd <= 0) return;
  const tau = t - WATER.t0, out = smooth(0, 4, tau) * (1 - smooth(WATER.t1 - WATER.t0 - 4, WATER.t1 - WATER.t0, tau));
  const x = Math.round(lerp(424, 412, out)), pour = tau > 6 && tau < 22;
  ctx.save(); ctx.beginPath(); ctx.rect(NEAR.x0, 0, W - NEAR.x0, NEAR.rail); ctx.clip();
  person(x, NEAR.deck - 1, WATERER, { view: 2, dir: -1, aR: pour ? 3 : 0 });
  ctx.restore();
  if (!pour) return;
  R(x - 8, 84, 4, 3, '#6a8a9a'); R(x - 10, 85, 2, 1, '#6a8a9a');               // the watering can over the rail
  ctx.fillStyle = '#cfe6f2';
  for (let i = 0; i < 9; i++) {
    const q = frac(t * 1.5 + i / 9), y = Math.round(lerp(88, NEAR.floor - 1, q * q)), dx = Math.round(Math.sin(i * 2.1) * 2);
    ctx.globalAlpha = 0.7; ctx.fillRect(x - 11 + dx, y, 1, 2);
  }
  ctx.globalAlpha = 1;
}
/* ----- small constant life: moths, pigeons on the parapet, the iguana, the cat, the paper lantern ----- */
const MOTHS = [...LANTERNS.map(x => [x, LANTERN_Y + 1, 1]), [N_BULB[0], N_BULB[1], 1.6], [RASPAO.bulb[0], RASPAO.bulb[1], 1], ...ARC_BULBS.map(([x, y]) => [x, y, 0.8])];
function drawMoths(t) {
  MOTHS.forEach(([cx, cy, sc], j) => {
    for (let i = 0; i < 3; i++) {
      const a = TAU * ((29 + i * 13 + j * 7) * t / LOOP + i * 0.33 + j * 0.1), r = (3 + i * 1.6) * sc;
      R(Math.round(cx + Math.cos(a) * r), Math.round(cy + Math.sin(a * 1.7) * r * 0.6), 1, 1, '#ecdfc4');
    }
  });
}
function drawPigeons(t) {
  PIGEONS.forEach((x, i) => {
    const y = wallTop(x) - 6, bob = (step(t, 480) + i * 3) % 7 === 0 ? 1 : 0;
    R(x - 1, y - 1, 3, 2, '#8a8a98'); R(x - 1, y - 1, 3, 1, '#a8a8b8'); R(x + (i & 1 ? -2 : 2), y - 2 + bob, 1, 1, '#9a9aa8');
  });
}
function drawIguana() {
  const { x, y } = IGUANA;
  R(x + 2, y + 2, 6, 2, '#4a7a34'); R(x + 3, y + 1, 4, 1, '#6a9a44'); R(x + 8, y + 2, 2, 1, '#5a8a3a'); R(x + 9, y + 1, 1, 1, '#2a2a20');
  R(x, y + 3, 2, 1, '#3a5a2a'); R(x - 1, y + 3, 1, 1, '#3a5a2a'); R(x + 3, y + 4, 1, 1, '#3a5a2a'); R(x + 7, y + 4, 1, 1, '#3a5a2a');
  R(x + 4, y, 2, 1, '#8ab85a');
}
function drawCat() {
  const { x, y } = CAT;
  R(x + 1, y + 2, 7, 3, '#c8803a'); R(x + 2, y + 1, 5, 1, '#d8904a'); R(x + 6, y, 2, 2, '#c8803a'); R(x + 6, y - 1, 1, 1, '#a86a30'); R(x + 8, y, 1, 1, '#a86a30');
  R(x, y + 4, 3, 1, '#a86a30'); R(x + 3, y + 3, 3, 1, '#e8a860');
}
function drawPaperLantern(t) {
  for (const [a, b] of LANTERN_UP) {
    if (t < a || t >= b) continue;
    const f = env(t, a, b, 3), q = (t - a) / (b - a), y = Math.round(lerp(PAPER.y + 17, PAPER.y + 2, smooth(0, 0.6, q))), x = Math.round(PAPER.x + 6 + wave(t, 40) * 2);
    ctx.globalAlpha = f;
    R(x - 2, y, 5, 5, '#ffb050'); R(x - 1, y, 3, 5, '#ffd88a'); R(x - 2, y + 5, 5, 1, '#c86a2a'); R(x, y + 2, 1, 2, '#fff2c0');
    halo(HL.paper, x, y + 3, f * 0.8);
    ctx.globalAlpha = 1;
  }
}
/* ----- the near horse's tail swishing, its ear flicking ----- */
function drawHorseTail(t) {
  const sw = wave(t, 40) * 0.7 + (frac(t / 15) < 0.08 ? Math.sin(frac(t / 15) / 0.08 * TAU) * 1.6 : 0);
  const [x0, y0] = cocheAt(48, 21);
  for (let k = 0; k <= 12; k++) {
    const q = k / 12, [x, y] = cocheAt(48 - 1.6 * q + sw * q * q * 1.4, 21 - 11 * q);
    R(x, y, 2, 1, k > 9 ? '#b8ae9e' : '#cdc4b6');
  }
  void x0; void y0;
  if (frac(t / 7.5) < 0.05) { const [ex, ey] = cocheAt(73.5, 33); R(ex, ey - 1, 1, 1, '#dcd4c8'); }
}

/* ================= Pre-render everything static ================= */
let COCHE2_C, COCHE2_SH, MIST_C, PALM_C, HL, SKY_C, MOON_C, WISP_C, FAR_C, PARAPET_C, NEAR_C, NEAR_STALL_C, WALL_C, STATUE_C, RASPAO_C, PORTAL_C, STALL_C, RAIL_C, GROUND_C, LAMP_C, TREE_C, COCHE_C, VIG_C, BB_C, BB_AD, PITCH;
function prerender() {
  HL = {
    moon: makeHalo(46, '#9ab4ff', 0.18, 1.8), moonCore: makeHalo(15, '#fff2d0', 0.3, 1.5),
    tower: makeHalo(70, '#ffd890', 0.13, 1.5), towerTop: makeHalo(26, '#fff0c0', 0.16, 1.5), dial: makeHalo(9, '#fff6dc', 0.35, 1.3),
    lantern: makeHalo(28, '#ffb860', 0.42, 1.5), lanternCore: makeHalo(5, '#ffe6b0', 0.6, 1.2), arch: makeHalo(16, '#ffb060', 0.22, 1.4),
    globe: makeHalo(30, '#ffb460', 0.22, 1.6), globeBig: makeHalo(46, '#ffb460', 0.2, 1.6), globeCore: makeHalo(5, '#fff0c8', 0.6, 1.2),
    bulb: makeHalo(16, '#ffc070', 0.32, 1.4), bbLamp: makeHalo(12, '#ffd8a0', 0.4, 1.5), room: makeHalo(14, '#ffb860', 0.2, 1.4),
    coche: makeHalo(6, '#ffe0a0', 0.5, 1.3), nearBulb: makeHalo(46, '#ffbe70', 0.26, 1.5), nearCore: makeHalo(8, '#fff0c8', 0.6, 1.2),
    dialStrike: makeHalo(18, '#fff2c8', 0.5, 1.4), fw: FW_COLS.map(c => makeHalo(40, c[0], 0.24, 1.5)), paper: makeHalo(8, '#ffc070', 0.45, 1.3), fwWall: FW_COLS.map(c => makeHalo(120, c[1], 0.1, 1.2)), phone: makeHalo(4, '#bfe0ff', 0.4, 1.2),
  };
  SKY_C = renderSky(); MOON_C = renderMoon(); WISP_C = WISPS.map(w => renderWisp(w.w, w.h, w.seed));
  FAR_C = renderFar();
  const wp = painter(W, H); renderWall(wp); renderTower(wp); WALL_C = wp.done();
  const sp = painter(W, H); renderStatue(sp); STATUE_C = sp.done();
  const rp = painter(W, H); renderRaspao(rp); renderTinto(rp); RASPAO_C = rp.done();
  PORTAL_C = renderPortal(); STALL_C = renderStalls(); RAIL_C = renderRail();
  PARAPET_C = renderParapet(); NEAR_C = renderNear(); NEAR_STALL_C = renderNearStall();
  GROUND_C = renderGround(); LAMP_C = renderLamps(); TREE_C = renderTree(); COCHE_C = renderCoche(COCHE.x, COCHE.g, COCHE.s, W, H, false);
  { const c = renderCoche(2, 33, 0.8, 70, 36), [f, fx] = makeCanvas(70, 36); fx.translate(70, 0); fx.scale(-1, 1); fx.drawImage(c, 0, 0); COCHE2_C = f; const [g, gx] = makeCanvas(70, 36); gx.drawImage(f, 0, 0); gx.globalCompositeOperation = 'source-in'; gx.fillStyle = SH; gx.fillRect(0, 0, 70, 36); COCHE2_SH = g; } VIG_C = renderVignette();
  BB_C = renderBillboard(); BB_AD = renderBillboardAd();
  PITCH = makePitch(BB.w, BB.h, { accent: '#b5179e' });
  MIST_C = MISTS.map((m, i) => renderMist(m.w, m.h, 61 + i * 7));
  { const pp = painter(W, H); for (const [cx, cy, sz, seed] of PALMS) renderPalm(pp.put, cx, cy, sz, seed, wallTop(cx) + 1); PALM_C = pp.done(); }
}

/* ================= Frame compositor (back to front) ================= */
// billboard: false leaves the sign out (the loop videos); weather: a WEATHERS pick, the city's own when left out;
// crowd: the share of the people shown (the city's own CROWD when left out); clock: the city's minutes after midnight
function renderFrame(t, { pitch = false, billboard = true, weather, reducedMotion = false, crowd = CROWD, clock } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  // sky: stars, the moon, wisps drifting
  rect(ctx, 0, 0, W, H, '#04061a');
  ctx.drawImage(SKY_C, 0, 0);
  for (const s of STARS) {
    ctx.globalAlpha = s.b * (0.5 + 0.5 * wave(t, s.k, s.ph));
    px(ctx, s.x, s.y, '#e8ecff');
    if (s.big) { px(ctx, s.x - 1, s.y, '#8890b8'); px(ctx, s.x + 1, s.y, '#8890b8'); }
  }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y); halo(HL.moonCore, MOON.x, MOON.y);
  ctx.drawImage(MOON_C, MOON.x - MOON.r, MOON.y - MOON.r);
  ctx.globalAlpha = 0.85;
  WISPS.forEach((w, i) => { const span = W + w.w, x = ((w.x + Math.floor(frac(t / LOOP + i * 0.37) * span)) % span) - w.w; ctx.drawImage(WISP_C[i], x, w.y); });
  ctx.globalAlpha = 1;
  const tg = 0.94 + 0.06 * wave(t, 12);
  halo(HL.tower, TX, 64, tg); halo(HL.towerTop, TX, 32, tg);
  drawFireworks(t, reducedMotion);
  drawPaperLantern(t);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  // beyond the wall: palms, the La Matuna rooftop and its sign
  for (let y = 88; y < 140; y += 2) {                                          // the palms beyond the wall, crowns swaying in the breeze
    const off = Math.round((wave(t, 40, y * 0.02) * 1.2 + wave(t, 97, 0.4) * 0.5) * clamp((126 - y) / 22, 0, 1));
    ctx.drawImage(PALM_C, 0, y, PORTAL.x0, 2, off, y, PORTAL.x0, 2);
  }
  ctx.drawImage(FAR_C, 0, 0);
  if (billboard) drawBillboard(t, pitch);
  // the wall walk: its outer parapet, people strolling up there, then the gate wall's face and the Torre del Reloj
  ctx.drawImage(PARAPET_C, 0, 0);
  drawWallWalk(t, crowd);
  ctx.drawImage(WALL_C, 0, 0);
  hands(ctx, TX, TR.dial, clock, ROCK_T, 3, 4, '#1e1820');
  halo(HL.dial, TX, TR.dial, 0.8);
  if (!reducedMotion) fireworkLight(t);
  drawBells(t); drawBats(t); drawPigeons(t); drawIguana();
  for (const L of LANTERNS) {
    const li = 0.78 + 0.16 * wave(t, 6 + (L & 7), L * 0.01) + 0.06 * wave(t, 480, L * 0.01);
    rect(ctx, L, LANTERN_Y - 1, 1, 4, '#ffe6b0');
    halo(HL.lantern, L, LANTERN_Y + 3, li); halo(HL.lanternCore, L, LANTERN_Y + 1, li);
  }
  for (const G of GATE) halo(HL.arch, G.x + (G.w >> 1), wallFoot(G.x) - 6, 0.85);
  drawSweeps(t);
  drawWallShadows(t, crowd);
  // the Portal de los Dulces and its nearest bay with the lit stall, then the wet plaza mirroring it all
  ctx.drawImage(PORTAL_C, 0, 0);
  ctx.drawImage(NEAR_C, 0, 0);
  const nb = 0.94 + 0.06 * wave(t, 720), sway = Math.round(wave(t, 30) * 1.4 + wave(t, 77, 0.3) * 0.6), bx0 = N_BULB[0] + sway;
  rect(ctx, N_BULB[0], 116, 1, 21, '#2a1e1c'); rect(ctx, N_BULB[0] + Math.round(sway / 2), 137, 1, 11, '#2a1e1c'); rect(ctx, bx0, 148, 1, N_BULB[1] - 150, '#2a1e1c');
  rect(ctx, bx0 - 1, N_BULB[1] - 2, 3, 4, '#fff4cc');
  halo(HL.nearBulb, bx0, N_BULB[1] + 4, nb); halo(HL.nearCore, bx0, N_BULB[1], nb);
  ctx.drawImage(GROUND_C, 0, 0);
  for (const Z of ZONES) reflect(ctx, t, { top: Z.top, rows: H - Z.top, squash: Z.sq, alpha: 0.32, amp: 0.55, grow: 0.02, k1: 36, k2: 90, x: Z.x, w: Z.w });
  ZONES.forEach((Z, zi) => {                                                   // the puddles: darker, then a clearer mirror
    if (!PUDDLES.some(q => q.zone === zi)) return;
    ctx.save(); puddleClip(zi);
    ctx.globalAlpha = 0.3; rect(ctx, Z.x, Z.top, Z.w, H - Z.top, '#120c12'); ctx.globalAlpha = 1;
    reflect(ctx, t, { top: Z.top, rows: H - Z.top, squash: Z.sq, alpha: 0.7, amp: 0.8, grow: 0.02, k1: 48, k2: 110, x: Z.x, w: Z.w });
    ctx.restore();
  });
  streak(ctx, t, TX, 12, '#ffe0a0', 0.16, 1.3, WALL_FOOT + 2, H);
  for (const L of LANTERNS) streak(ctx, t, L, 3, '#ffb860', 0.26, L, wallFoot(L) + 1, H);
  for (const L of LAMPS) streak(ctx, t, L.x, L.h > 80 ? 6 : 4, '#ffb460', 0.3, L.x * 0.7, L.base + 1, H);
  for (const [bx] of ARC_BULBS) streak(ctx, t, bx, 4, '#ffc070', 0.22, bx, PORTAL.floor + 3, H);
  streak(ctx, t, bx0, 10, '#ffc878', 0.3 * nb, 4.4, NEAR.floor + 5, H);
  drawDrips(t);
  drawMist(t);
  if (wx.petals) drawPetals(t, 0);
  // the plaza's mid-ground: the passing coche and strollers, the statue, the raspao cart, the tinto cart, people
  drawCoche2(t, crowd);
  drawPlazaWalkers(t, crowd);
  ctx.drawImage(STATUE_C, 0, 0);
  const rf = frac(t / 12), vaR = rf < 0.5 ? ((step(t, 1920) & 1) ? 4 : 0) : rf < 0.75 ? 3 : 0;
  if (crowd > 0) {
    person(VENDOR.x, VENDOR.y, VENDOR.P, { view: 1, aR: vaR });
    if (vaR === 3) R(VENDOR.x + 6, 183, 1, 4, ['#e8283a', '#3ac850', '#f2d23a', '#8a3ac8'][Math.floor(t / 12) % 4]);
  }
  ctx.drawImage(RASPAO_C, 0, 0);
  rect(ctx, RASPAO.bulb[0] - 1, RASPAO.bulb[1] - 1, 3, 2, '#fff2c0'); halo(HL.bulb, RASPAO.bulb[0], RASPAO.bulb[1], 0.95 + 0.05 * wave(t, 600));
  CAST.forEach((c, i) => {
    if (c.rank >= crowd || (c.rank === 0 && crowd <= 0)) return;
    const o = poseOf(c, ACTS[i], t), hy = person(c.x, c.y - (o.hop || 0), c.P, o);
    if (o.aR === 2) halo(HL.phone, c.x + 3, hy, 0.9);
    if (ACTS[i] === 'girl' && o.aR === 3) { R(c.x - 4, 192, 1, 2, '#e8283a'); R(c.x - 4, 194, 1, 1, '#e8c890'); }
    if (ACTS[i] === 'tinto' && o.aR === 3) { R(c.x - 4, 186, 1, 1, '#f4f0e6'); R(c.x - 4, 184, 1, 2, '#3a2418'); }
  });
  drawBowl(316, 204);
  // under the far arcade: the DULCES stall, its seller and a customer, the neighbour in the balcony door, the bulbs
  if (crowd > 0) { const hy = person(SELLER.x, SELLER.y, SELLER.P, { sit: 1, view: 1, aR: 4 }); R(SELLER.x + 3, hy + 1 + (step(t, 960) & 1), 2, 2, '#f2c84a'); }
  ctx.drawImage(STALL_C, 0, 0);
  drawCat();
  if (BUYER.rank < crowd) person(BUYER.x, BUYER.y, BUYER.P, { aR: 3 });
  halo(HL.room, 393, 140, 0.9);
  if (NEIGHBOUR.rank < crowd) person(NEIGHBOUR.x, NEIGHBOUR.y, NEIGHBOUR.P, { view: 1, aL: 4 });
  ctx.drawImage(RAIL_C, 0, 0);
  ARC_BULBS.forEach(([bx, by], i) => { rect(ctx, bx, by - 4, 1, 3, '#2a2026'); rect(ctx, bx - 1, by - 1, 3, 2, '#fff2c0'); halo(HL.bulb, bx, by, 0.92 + 0.08 * wave(t, 360, i * 0.27)); });
  // the near stall: its seller behind the jars, a mother choosing and her daughter reaching up
  if (crowd > 0) { const hy = person(NSELLER.x, NSELLER.y, NSELLER.P, { sit: 1, view: 1, aR: 4 }); R(NSELLER.x + 4, hy + 1 + (step(t, 960) & 1), 3, 3, '#e85a7a'); }
  ctx.drawImage(NEAR_STALL_C, 0, 0);
  if (NBUYER.rank < crowd) {
    const bag = frac(t / 20) > 0.8, hop = frac(t / 6) < 0.08 ? 1 : 0, look = fwOn(t);
    person(NBUYER.x, NBUYER.y, NBUYER.P, { aR: bag ? 0 : 3, aL: look ? 1 : 0 });
    if (bag) R(NBUYER.x + 4, 188, 3, 4, '#f4ecd8');
    person(NKID.x, NKID.y - hop, NKID.P, { aR: 1 });
  }
  drawWatering(t, crowd);
  drawMoths(t);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  if (wx.petals) drawPetals(t, 1);
  // the almond tree and the lamps nearest us, their globes
  for (let y = 56; y < 160; y += 8) { const off = Math.round(wave(t, 24, y * 0.013) * (160 - y) / 70 + wave(t, 61, y * 0.04) * 0.4); ctx.drawImage(TREE_C, 0, y, 70, 8, off, y, 70, 8); }
  ctx.drawImage(TREE_C, 0, 160, 70, H - 160, 0, 160, 70, H - 160);
  ctx.drawImage(LAMP_C, 0, 0);
  for (const L of LAMPS) {
    const big = L.h > 80, r = big ? 2 : 1;
    lampGlobes(L).forEach(([gx, gy]) => { rect(ctx, gx - r + (big ? 0 : 1), gy - r + (big ? 0 : 1), big ? 4 : 2, big ? 4 : 2, '#fff4d8'); halo(HL.globeCore, gx, gy, 0.9); });
    halo(big ? HL.globeBig : HL.globe, L.x, lampTop(L) + lampS(L), 0.95 + 0.05 * wave(t, 300, L.x * 0.01));
  }
  // the coche waiting close to us, its lamp, its coachman on the box
  ctx.drawImage(COCHE_C, 0, 0);
  drawHorseTail(t);
  rect(ctx, COCHE_LAMP[0], COCHE_LAMP[1] - 2, 1, 3, '#fff2c8'); halo(HL.coche, COCHE_LAMP[0], COCHE_LAMP[1], 0.95);
  if (crowd > 0) person(DRIVER.x, DRIVER.y, DRIVER.P, { sit: 1, view: 2, dir: 1, aR: t > 47 && t < 51 ? 1 : 3, shin: 8, lean: frac(t / 20) > 0.55 && frac(t / 20) < 0.8 ? 1 : 0 });
  if (wx.petals) drawPetals(t, 2);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'cartagena',
  name: 'Cartagena',
  tagline: 'Clock tower over the old walls',
  timeZone: 'America/Bogota',
  accent: '#b5179e',
  focusX: TX,
  landmark: [TX - 31, TX + 31],                        // the Torre del Reloj's base stage, its full width
  landmarkTop: TR.vane,                                // the weathervane's tip
  billboard: BB,
  crowd: CROWD,
  spots: [                                             // what answers a tap (site/js/ui/spots.js)
    { kind: 'bell', x: TX - 19, y: TR.gab, w: 39, h: TR.clkBot - TR.gab + 1 },                     // the clock stage and belfry
    { kind: 'birds', x: PIGEONS[0] - 3, y: wallTop(PIGEONS[0]) - 9, w: PIGEONS[3] - PIGEONS[0] + 6, h: 5 },   // pigeons on the parapet
    { kind: 'window', x: 417, y: 59, w: 15, h: 48 },                                                 // the near balcony's open door
  ],
  ambience: { crickets: 0.35, crowd: 0.3, traffic: 0.2, wind: 0.2, water: 0.08 },
  events: [
    { t: 26, type: 'chime', x: 180, gain: 0.25 },
    ...BELLS.map(t => ({ t, type: 'bell', x: TX, gain: 0.55 })),
    ...FIREWORKS.map(([t, x]) => ({ t, type: 'firework', x, gain: 0.45 })),
    { t: 207, type: 'splash', x: 412, gain: 0.12 },
  ],
  create(c) {
    ctx = c;
    prerender();
    return { render: renderFrame };
  },
};
